import { computed, defineAsyncComponent, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import { deepEqual } from "fast-equals";
import debounce from "lodash/debounce";

import { Unit } from "@/enums/datetime";
import { convertToArray, getObjectKeys, stringCompare } from "@/helpers/common";
import {
  dropDownTaskPriorityLevelsOptions,
  dropDownTaskStatusesOptions,
} from "@/helpers/communication";
import {
  ADDITIONAL_TRACKING_TOKENS,
  ANALYST,
  ANALYST_IDS,
  CLIENT_ANALYST,
  CLIENT_FUNDING_ADVISOR,
  CLIENT_TRACKING_TOKENS,
  CLIENT_UNDERWRITER,
  DISCOVERY_FILTER_OPTIONS,
  FUNDERS_WITH_OFFERS,
  FUNDERS_WITH_PLACEMENTS,
  FUNDING_ADVISOR,
  FUNDING_ADVISOR_IDS,
  NOT_ADDITIONAL_TRACKING_TOKENS,
  NOT_ANALYST_IDS,
  NOT_CLIENT_TRACKING_TOKENS,
  NOT_FUNDERS_WITH_OFFERS,
  NOT_FUNDERS_WITH_PLACEMENTS,
  NOT_FUNDING_ADVISOR_IDS,
  NOT_ORIGIN_PARTNERS,
  NOT_PARTNERS,
  NOT_PRODUCT_TYPE_FUNDED,
  NOT_TASK_ASSIGNEES,
  NOT_TASK_PRIORITY_LEVELS,
  NOT_TASK_STATUSES,
  NOT_UNDERWRITER_IDS,
  NOT_WORKFLOW_STATUSES,
  NOT_WORKFLOW_SUB_STATUSES,
  NOT_WORKFLOW_TEMPLATE_IDS,
  ORIGIN_PARTNERS,
  PARTNERS,
  PRODUCT_TYPE_FUNDED,
  STATE_NAMES,
  STATUS_PLACEMENT,
  STATUS_UNDERWRITING,
  TASK_ASSIGNEES,
  TASK_PRIORITY_LEVELS,
  TASK_STATUSES,
  UNDERWRITER,
  UNDERWRITER_IDS,
  WORKFLOWS,
  WORKFLOW_STATUSES,
  WORKFLOW_SUB_STATUSES,
  WORKFLOW_TEMPLATE_IDS,
  YES_NO_OPTIONS,
} from "@/helpers/constants";
import { STAGE_MANAGEMENT_OPTIONS } from "@/helpers/constants/workflow";
import {
  createOptionsUpdatedHandler,
  getFundersDropdownOptions,
} from "@/helpers/deals";
import { filterHasValue } from "@/helpers/filters";
import {
  formatDate,
  formatMonth,
  formatTimeElapsed,
  formatUserFullName,
  moneyToTwoDecimal,
  truncateWithEllipsis,
} from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { useDealsFilters } from "@/hooks/deals";
import { useIndustryOptions, useNumberOfEmployees } from "@/hooks/options";
import { useWorkflowCategoriesInfo } from "@/hooks/workflow";
import applicationsApiService from "@/services/modules/applications";
import { getApprovedClients } from "@/services/modules/clients";
import clientsApiService from "@/services/modules/clients";
import customizationApiService from "@/services/modules/customization";
import fundersApiService from "@/services/modules/funders";
import offerTemplateService from "@/services/modules/offerTemplate";
import scorecardsApiService from "@/services/modules/scorecards";
import usersApiService from "@/services/modules/users";
import workflowTemplateService from "@/services/modules/workflows";
import workflowApiService from "@/services/modules/workflows";
import DealsDataWorker from "@/workers/dealsDataWorker?worker";

import HasDataDisplay, {
  type EmitPayload,
  EnrichmentData,
} from "@/components/HasDataDisplay.vue";
import PriorityOption from "@/components/communicationLogs/filters/taskFilters/PriorityOption.vue";
// Component imports
import AdditionalTokenDisplay from "@/components/deals/AdditionalTokenDisplay.vue";
import CallCountText from "@/components/deals/CallCountText.vue";
import CallsFilter from "@/components/deals/CallsFilter.vue";
import DocumentsFilter from "@/components/deals/DocumentsFilter.vue";
import LastCall from "@/components/deals/LastCall.vue";
import LastEmailSent from "@/components/deals/LastEmailSent.vue";
import MinAndMaxFilter from "@/components/deals/MinAndMaxFilter.vue";
import RecentNote from "@/components/deals/RecentNote.vue";
import ReferrerHostDisplay from "@/components/deals/ReferrerHostDisplay.vue";
import TruncatedTooltipableText from "@/components/deals/TruncatedTooltipableText.vue";
import WorkflowStatusSelector from "@/components/deals/WorkflowStatusSelector.vue";
import DatesList from "@/components/deals/nestedColumns/DatesList.vue";
import DeadReasonList from "@/components/deals/nestedColumns/DeadReasonList.vue";
import DescriptionsList from "@/components/deals/nestedColumns/DescriptionsList.vue";
import NameList from "@/components/deals/nestedColumns/NameList.vue";
import PlacementStatusList from "@/components/deals/nestedColumns/PlacementStatusList.vue";
import PriorityLevelList from "@/components/deals/nestedColumns/PriorityLevelList.vue";
import ProductTypeList from "@/components/deals/nestedColumns/ProductTypeList.vue";
import RiskTierList from "@/components/deals/nestedColumns/RiskTierList.vue";
import SubmissionList from "@/components/deals/nestedColumns/SubmissionsList.vue";
import WorkflowStatusList from "@/components/deals/nestedColumns/WorkflowStatusList.vue";
import BadgeList from "@/components/ui/BadgeList.vue";
import ClickToCopyIdColumn from "@/components/ui/ClickToCopyIdColumn.vue";
import DataOrchestrationMasterStatusDropdown from "@/components/ui/DataOrchestrationMasterStatusDropdown.vue";
import DealCommunicationLog from "@/components/ui/DealCommunicationLog.vue";
import DealModeDropdown from "@/components/ui/DealModeDropdown.vue";
import ScoreCardResultStatusPill from "@/components/ui/ScoreCardResultStatusPill.vue";
import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";
import NaicsCodesDropdown from "@/components/ui/inputs/NaicsCodesDropdown.vue";
import CheckmarkCell from "@/components/ui/table/cell/CheckmarkCell.vue";
import DealStageCell from "@/components/ui/table/cell/DealStageCell.vue";
import EmailValidationMessageCell from "@/components/ui/table/cell/EmailValidationMessageCell.vue";
import ScorecardStatusCell from "@/components/ui/table/cell/ScorecardStatusCell.vue";
import StageManagementCell from "@/components/ui/table/cell/StageManagementCell.vue";
import SubIndustryCell from "@/components/ui/table/cell/SubIndustryCell.vue";
import TaskAssignee from "@/components/ui/table/cell/TaskAssignee.vue";
import TaskPriority from "@/components/ui/table/cell/TaskPriority.vue";
import TaskStatus from "@/components/ui/table/cell/TaskStatus.vue";
import UtmParameters from "@/components/ui/table/cell/UtmParameters.vue";
import DateAndTimeDisplay from "@/components/ui/text/DateAndTimeDisplay.vue";
import LfTag from "@/components/ui/text/LfTag.vue";
import AssignUserField from "@/views/deals/components/AssignUserField.vue";
import DealNameField from "@/views/deals/components/DealNameField.vue";
import DealWorkflowField from "@/views/deals/components/DealWorkflowField.vue";
import DiscoverStatusDropdown from "@/views/deals/components/DiscoverStatusDropdown.vue";
import FilterByUser from "@/views/deals/components/FilterByUser.vue";
import RangeFilter from "@/views/deals/components/smartFilters/RangeFilter.vue";

import type { TaskStatuses } from "@/enums/communicationLogs";
import type { IApplication } from "@/models/applications";
// End component imports

// Type imports
import type {
  ActiveFilterWithExceptionMode,
  CallsFilterPayload,
  IDealsColumn,
  IDealsFilters,
  WorkflowSnapshotStatus,
} from "@/models/applications";
import type { IClient } from "@/models/clients";
import type { RequiredDocument } from "@/models/common";
import type { CommunicationLogBase, TaskLog } from "@/models/communicationLogs";
import type { ILenderPlacement, IOffer } from "@/models/funders";
import type { IFunder } from "@/models/funders";
import type { OfferTemplate } from "@/models/offers";
import type {
  IApplicationStatus,
  MultiSelectOption,
  UseOfFundsOption,
} from "@/models/options";
import type {
  DataOrchestrationMasterStatus,
  DataOrchestrationMasterStatusType,
} from "@/models/orchestration";
import type { IScorecard, IScorecardGroup } from "@/models/scorecards";
import type { IUser } from "@/models/users";
import type {
  IWorkflow,
  StageNameEntry,
  WorkflowsViewFilters,
} from "@/models/workflows";
import type {
  EnrichedDealOutput as EnrichedDeal,
  RawWorkerInput,
} from "@/workers/dealsDataWorker";
import type { ComputedRef, Ref } from "vue";

// End type imports

const ContactItem = defineAsyncComponent(
  () => import("@/views/deals/components/contacts/ContactItem.vue")
);

const PRODUCT_TYPES = {
  "0": "NextGen",
  "1": "Legacy",
};

const DEAL_MODES = {
  1: "Live",
  2: "Test",
};

interface UseDealsColumns {
  // Without the UseDealsColumns return type the serializer reaches the maximum infer depth
  allColumns: ComputedRef<IDealsColumn[]>;
  appliedWorkflows: Ref<string[]>;
  subColumnLoading: Ref<{
    has_placement_loading: boolean;
    has_offer_loading: boolean;
    closest_due_task_loading: boolean;
  }>;
  subColumnsVisibility: Ref<{
    has_placement: boolean;
    has_offer: boolean;
    closest_due_task: boolean;
  }>;
  enrichedDealsData: Ref<EnrichedDeal[]>;
}

export const useDealsColumns = (filters: IDealsFilters): UseDealsColumns => {
  const { getters } = useStore();
  const { t } = useI18n();
  const { industryTypeLabel, industryTypesOptions } = useIndustryOptions();
  const route = useRoute();
  const router = useRouter();

  const {
    fundersOptions,
    handleFundersOptionsUpdate,
    assigneeOptions,
    handleAssigneesOptionsUpdate,
    scorecardsOptions,
    handleScorecardsOptionsUpdate,
    scorecardGroupsOptions,
    handleScorecardGroupsOptionsUpdate,
    workflowStatuses,
    handleWorkflowStatusesOptionsUpdate,
    workflowSubStatuses,
    handleWorkflowSubStatusesOptionsUpdate,
    excludableFiltersMap,
    productTypes,
  } = useDealsFilters();

  const {
    numberOfEmployeesOptionsLabels,
    numberOfEmployeeIdByIndex,
    numberOfEmployeeIndexById,
  } = useNumberOfEmployees();

  const {
    isLendflowUser,
    isFundingAdvisor,
    isClientFundingAdvisor,
    isClientUnderwriter,
    isClientAdmin,
    isClient,
    isClientAnalyst,
    isAdmin,
    isAnalyst,
  } = useAuth();

  const { workflowCategoriesDropdownOptions } = useWorkflowCategoriesInfo();

  const partners = ref<Record<string, string>>({});
  const originPartners = ref<Record<string, string>>({});
  const submissionPartners = ref<Record<string, string>>({});
  const workflowTemplates = ref<Record<string, string>>({});
  const stageNamesOptions = ref<Record<string, string>>({});
  const productsRequestedOptions = ref<Record<string, string>>({});
  const lastEmailSentNames = ref<Record<string, string>>({});
  const fundingLenders = ref<Record<string, string>>({});
  const clientTrackingTokens = ref<string[]>([]);
  const additionalTrackingTokens = ref<string[]>([]);
  const utmParameters = ref<string[]>([]);
  const referrerHosts = ref<Record<string, string>>({});

  const formatter = ref({
    date: "MM/DD/YYYY",
    month: "MMM",
  });
  const appliedWorkflows = ref<string[]>([]);
  const subColumnsVisibility = ref({
    has_placement: false,
    has_offer: false,
    closest_due_task: false,
  });

  const subColumnLoading = ref({
    has_placement_loading: false,
    has_offer_loading: false,
    closest_due_task_loading: false,
  });

  const dealsData = computed<IApplication[]>(() => getters["applications/all"]);

  const dealsOfferData = ref<Record<IApplication["id"], IOffer[]>>({});

  const dealsPlacementData = ref<
    Record<IApplication["id"], ILenderPlacement[]>
  >({});

  const dealsTaskData = ref<Record<IApplication["id"], TaskLog[]>>({});

  const enrichedDealsData = ref<EnrichedDeal[]>([]);
  const isEnriching = ref(false);

  const worker = new DealsDataWorker();

  let lastWorkerInput: RawWorkerInput | null = null;

  const processEnrichment = debounce(
    ([currentDeals, currentPlacements, currentOffers, currentTasks]: [
      IApplication[],
      Record<string, ILenderPlacement[]>,
      Record<string, IOffer[]>,
      Record<string, TaskLog[]>,
    ]) => {
      if (!currentDeals?.length) {
        enrichedDealsData.value = [];
        isEnriching.value = false;
        lastWorkerInput = null;
        return;
      }

      const workerInput: RawWorkerInput = {
        deals: currentDeals,
        placementsByDealId: currentPlacements,
        offersByDealId: currentOffers,
        tasksByDealId: currentTasks,
      };

      // Skip processing if input hasn't changed
      if (lastWorkerInput && deepEqual(workerInput, lastWorkerInput)) {
        return;
      }

      isEnriching.value = true;
      lastWorkerInput = JSON.parse(JSON.stringify(workerInput));
      worker.postMessage(JSON.parse(JSON.stringify(workerInput)));
    },
    100
  );

  watch(
    [dealsData, dealsPlacementData, dealsOfferData, dealsTaskData],
    processEnrichment,
    { deep: true, immediate: true }
  );

  worker.onmessage = (
    event: MessageEvent<{
      enrichedDeals: EnrichedDeal[];
      error?: { message: string };
    }>
  ) => {
    const { enrichedDeals, error } = event.data;

    if (error) {
      console.error("[useDealsColumns] Worker error:", error.message);
    } else if (enrichedDeals) {
      enrichedDealsData.value = enrichedDeals;
    }
    isEnriching.value = false;
  };

  worker.onerror = (error) => {
    console.error("[useDealsColumns] Unhandled worker error:", error);
    isEnriching.value = false;
  };

  onUnmounted(() => {
    // Clean up all data before terminating worker
    enrichedDealsData.value = [];
    dealsOfferData.value = {};
    dealsPlacementData.value = {};
    dealsTaskData.value = {};
    lastWorkerInput = null;
    worker.terminate();
  });

  const saveOfferData = (data: EmitPayload<IOffer>) => {
    dealsOfferData.value[data.applicationId] = data.enrichmentData;
    subColumnsVisibility.value.has_offer = true;
  };

  const savePlacementData = (data: EmitPayload<ILenderPlacement>) => {
    dealsPlacementData.value[data.applicationId] = data.enrichmentData;
    subColumnsVisibility.value.has_placement = true;
  };

  const saveTaskData = (data: EmitPayload<CommunicationLogBase>) => {
    dealsTaskData.value[data.applicationId] = data.enrichmentData as TaskLog[];
    subColumnsVisibility.value.closest_due_task = true;
  };

  const dealsLoading = computed<boolean>(() => getters["applications/loading"]);

  const revenueRanges = computed<Record<string, string>>(
    () => getters["options/revenueRanges"] || {}
  );

  const timeZones = computed<Record<string, string>>(
    () => getters["options/timeZones"]
  );

  const offerStatuses = computed<Record<string, string>>(
    () => getters["options/offerStatuses"]
  );

  const creditScores = computed<Record<string, string>>(
    () => getters["options/creditScores"]
  );

  const latestScoreCardStatuses = computed<Record<string, string>>(
    () => getters["options/latestScoreCardStatuses"]
  );

  const fundsNeededTimesOptions = computed<Record<string, string>>(
    () => getters["options/fundsNeededTimes"] || {}
  );

  const client = computed<IClient | null>(
    () => getters["auth/authClientSettings"]
  );

  const authClient = computed<IClient>(
    () => getters["auth/authClientSettings"]
  );

  const canUpdateDiscoveryStatus = computed(
    () => isLendflowUser || isClientAdmin || isClientUnderwriter
  );

  const canSeeAsLendflow = computed(() => {
    const clientCanCreateUnderwriter = !!client.value?.can_create_underwriter;
    return (
      (isClientAdmin ||
        isClientUnderwriter ||
        isClientAnalyst ||
        isClientFundingAdvisor) &&
      clientCanCreateUnderwriter
    );
  });

  const dataOrchestrationMasterStatuses = computed<
    Record<DataOrchestrationMasterStatusType, DataOrchestrationMasterStatus>
  >(() => getters["options/dataOrchestrationMasterStatuses"]);

  const numberOfEmployeesIndices = computed(() =>
    filters.number_of_employees.flatMap((index) =>
      String(numberOfEmployeeIndexById(Number(index)))
    )
  );

  const isLendflowTable = computed(
    () => isLendflowUser || canSeeAsLendflow.value
  );

  const documentsFilterLabel = computed(
    () =>
      `${t("STIPS.TITLE")}${filters.completed_documents.length ? "-" : ""}${
        filters.completed_documents == "1"
          ? t("DEALS.FILTERS.COMPLETE")
          : filters.completed_documents == "0"
            ? t("DEALS.FILTERS.INCOMPLETE")
            : ""
      }`
  );

  const applicationStatuses = computed(() => {
    const statusesValue: IApplicationStatus[] =
      getters["options/applicationStatuses"];

    if (cantSeeCompleteAndOfferTabs.value) {
      // Status 3 = Offer Out, 8 = Complete
      return statusesValue.filter(
        (status) => status.id === 3 || status.id === 8
      );
    }
    return statusesValue;
  });

  const applicationStatusesArray = computed(() =>
    applicationStatuses.value.map(({ status }) => status)
  );

  const selectedDealStatusIdsToIndexes = computed(() => {
    // Create a Map for O(1) lookup instead of O(n) findIndex for each status
    const statusIdToIndexMap = new Map<number, number>();
    applicationStatuses.value.forEach((status, index) => {
      statusIdToIndexMap.set(status.id, index);
    });

    return filters.statuses.map((statusId) => {
      const index = statusIdToIndexMap.get(Number(statusId));
      return (index ?? -1).toString();
    });
  });

  const stageCategories = computed<Record<string, string>>(
    () => getters["options/stageCategories"]
  );

  const workflowSegmentOptions = computed(() => {
    const types: MultiSelectOption[] = getters["options/workflowSegments"];
    return types.reduce<Record<string, string>>((acc, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    }, {});
  });

  const automationOutcomes = computed<Record<string, string>>(
    () => getters["options/automationOutcomes"]
  );

  const cantSeeCompleteAndOfferTabs = computed(
    () =>
      (isClientUnderwriter || isClientAdmin || isClientAnalyst) &&
      !client.value?.can_create_underwriter
  );

  const canAssignAnalyst = computed(() => isAdmin || isAnalyst);

  const availableSources = computed<Record<string, string>>(
    () => getters["options/sources"]
  );

  const states = computed<Record<string, string>>(
    () => getters["options/states"]
  );

  const formatDealSource = (source: number) => {
    return (
      {
        1: t("DEALS.SOURCE.API"),
        2: t("DEALS.SOURCE.WIDGET"),
        3: t("DEALS.SOURCE.RENEWAL"),
        5: t("DEALS.SOURCE.DASHBOARD"),
        6: t("DEALS.SOURCE.PLACEMENT_LENDFLOW"),
        7: t("DEALS.SOURCE.PLACEMENT_EMAIL"),
        8: t("DEALS.SOURCE.PLACEMENT_MANUAL"),
        9: t("DEALS.SOURCE.PLACEMENT_API"),
      }[source] || "-"
    );
  };
  const formatDealType = (isLegacy: boolean) =>
    isLegacy ? "Legacy" : "NextGen";

  const handleClientTokensUpdate = (
    value: string[],
    type: "search" | "loadmore"
  ) => {
    if (type === "search") {
      clientTrackingTokens.value = value;
      return;
    }

    clientTrackingTokens.value = [...clientTrackingTokens.value, ...value];
  };

  const handleAdditionalTokensUpdate = (
    value: { token: string }[],
    type: "search" | "loadmore"
  ) => {
    if (type === "search") {
      additionalTrackingTokens.value = value.map((item) => item.token);
      return;
    }

    const newTokens = value.map((item) => item.token);
    additionalTrackingTokens.value = [
      ...additionalTrackingTokens.value,
      ...newTokens,
    ];
  };

  const handleUtmParamsUpdate = (
    value: string[],
    type: "search" | "loadmore"
  ) => {
    if (type === "search") {
      utmParameters.value = value;
      return;
    }

    const nextPageValues = value.reduce((res: Record<string, string>, item) => {
      res[item] = item;
      return res;
    }, {});

    utmParameters.value = { ...utmParameters.value, ...nextPageValues };
  };

  const handleLastEmailSentNameOptionsUpdate =
    createOptionsUpdatedHandler(lastEmailSentNames);

  const handleFundingLendersOptionsUpdate =
    createOptionsUpdatedHandler(fundingLenders);

  const handleReferrerHostsOptionsUpdate = createOptionsUpdatedHandler(
    referrerHosts,
    "host_name"
  );

  const handlePartnersOptionsUpdate = createOptionsUpdatedHandler(partners);

  const handleOriginPartnersOptionsUpdate =
    createOptionsUpdatedHandler(originPartners);

  const handleSubmissionPartnersOptionsUpdate =
    createOptionsUpdatedHandler(submissionPartners);

  const handleStageNamesOptionsUpdate = createOptionsUpdatedHandler(
    stageNamesOptions,
    "stage_name"
  );

  const handleProductsRequested = createOptionsUpdatedHandler(
    productsRequestedOptions,
    "name"
  );

  const handleWorkflowTemplatesOptionsUpdate =
    createOptionsUpdatedHandler(workflowTemplates);

  const formatStateName = (state: string) => {
    return state in STATE_NAMES
      ? STATE_NAMES[state as keyof typeof STATE_NAMES]
          .toLowerCase()
          .split(" ")
          .map((word: string) =>
            word ? word[0].toUpperCase() + word.substr(1) : ""
          )
          .join(" ")
      : state || "";
  };

  const sortByChange = (value: string) => {
    if (filters.sort_by !== value) {
      filters.sort_by = value;
      return;
    }
    filters.sort_by = `-${value}`;
  };

  const formatUseOfFunds = (useOfFundsId: string) => {
    const getUseOfFunds: UseOfFundsOption = getters["options/useOfFunds"];
    return useOfFundsId ? getUseOfFunds(Number(useOfFundsId))?.toString() : "-";
  };

  const formatIndustryTypes = (industryId: string) =>
    industryTypeLabel(Number(industryId)) || "-";

  const allColumns = computed<IDealsColumn[]>(() => {
    // Please be careful when adding/modifying the columns.
    // Because of its size it's hard to read and easy to make mistakes (especially TS ones).
    const filteredColumns = [
      {
        label: t("DEALS.TITLE"),
        key: "business_name",
        component: DealNameField,
        props: {
          includeBackTo: true,
        },
        columnClass: "w-full min-w-64 deal-name",
      },
      isLendflowUser || isClient
        ? {
            label: t("DEALS.TABLE_HEADERS.DEAL_MODE"),
            key: "deal_mode",
            component: DealModeDropdown,
            props: {
              isDealsListView: true,
            },
            filterComponent: {
              initialRender: false,
              component: LfCheckboxDropdown,
              props: {
                modelValue: filters.deal_modes,
                options: DEAL_MODES,
                name: "deal_modes",
                placeholder: t("DEALS.TABLE_HEADERS.DEAL_MODE"),
                showClearFilter: true,
                filterIcon: true,
              },
              plural: t("DEALS.TABLE_HEADERS.DEAL_MODES"),
              onValueChange: (value: string[]) => (filters.deal_modes = value),
              numberOfFilters: filters.deal_modes.length,
            },
          }
        : null,
      {
        label: t("DEALS.TABLE_HEADERS.DEAL_STAGE"),
        key: "status",
        component: DealStageCell,
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: selectedDealStatusIdsToIndexes.value,
            options: applicationStatusesArray.value,
            name: "statuses",
            placeholder: t("DEALS.TABLE_HEADERS.DEAL_STAGE"),
            showClearFilter: true,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.DEAL_STAGES"),
          onValueChange: (indexes: number[]) => {
            const selectedStatusIds = indexes.map((index) =>
              applicationStatuses.value[index].id.toString()
            );
            filters.statuses = selectedStatusIds;
          },
          numberOfFilters: filters.statuses.length,
        },
      },
      {
        label: t("SMART_FILTERS.NUMBER_OF_CALLS", 1),
        key: "number_of_call_sessions",
        component: CallCountText,
        filterComponent: {
          initialRender: false,
          component: CallsFilter,
          props: {
            name: "number_of_call_sessions",
            numberOfCallsRange: filters.number_of_calls,
            dateRange: filters.created_at_call,
            showClearFilter: true,
            filterIcon: true,
            alwaysShow: true,
          },
          onChange: (value: CallsFilterPayload) => {
            filters.number_of_calls = value.number_of_calls;
            filters.created_at_call = value.created_at_dates;
          },
          hasFiltersSet:
            !!filters.number_of_calls.length ||
            !!filters.created_at_call.length,
          plural: t("SMART_FILTERS.NUMBER_OF_CALLS", 0),
          sortByChange,
          sortBy: filters.sort_by,
        },
      },
      isLendflowUser ||
      (isClient && !!authClient.value?.can_manage_data_orchestration)
        ? {
            label: t("DEALS.TABLE_HEADERS.UNDERWRITING_STATUS"),
            key: "data_orchestration_log.master_status",
            component: DataOrchestrationMasterStatusDropdown,
            props: {
              showDropdown: false,
            },
            filterComponent: {
              initialRender: false,
              component: LfCheckboxDropdown,
              props: {
                modelValue: filters.master_statuses,
                options: dataOrchestrationMasterStatuses.value,
                name: "master_statuses",
                placeholder: t("DEALS.TABLE_HEADERS.UNDERWRITING_STATUS"),
                showClearFilter: true,
                filterIcon: true,
              },
              plural: t("DEALS.TABLE_HEADERS.UNDERWRITING_STATUSES"),
              onValueChange: (value: string[]) =>
                (filters.master_statuses = value),
              numberOfFilters: filters.master_statuses.length,
            },
          }
        : null,
      {
        label: t("DEALS.TABLE_HEADERS.STAGE_CATEGORY"),
        key: "stage_category",
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.stage_categories,
            options: stageCategories.value,
            placeholder: t("DEALS.TABLE_HEADERS.STAGE_CATEGORY", 2),
            name: "stage_categories",
            showClearFilter: true,
            filterIcon: true,
            lazyLoad: true,
            valueIsOption: true,
          },
          plural: t("DEALS.TABLE_HEADERS.STAGE_CATEGORY", 2),
          onValueChange: (value: string[]) => {
            filters.stage_categories = value;
          },
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.stage_categories.length,
        },
      },
      {
        label: t("WORKFLOW.WORKFLOW_SEGMENT"),
        key: "workflow_segment",
        component: LfTag,
        props: { class: "capitalize" },
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.workflow_segments,
            options: workflowSegmentOptions.value,
            placeholder: t("WORKFLOW.WORKFLOW_SEGMENT"),
            name: "workflow_segments",
            filterIcon: true,
            showClearFilter: true,
            lazyLoad: true,
          },
          onValueChange: (value: string[]) => {
            filters.workflow_segments = value;
          },
          plural: t("WORKFLOW.WORKFLOW_SEGMENT"),
          numberOfFilters: filters.workflow_segments.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.STAGE_NAME"),
        key: "stage_name",
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.stage_names,
            options: stageNamesOptions.value,
            placeholder: t("DEALS.TABLE_HEADERS.STAGE_NAME"),
            name: "stage_names",
            enableSearch: true,
            showClearFilter: true,
            getPaginatedOptions: (params: WorkflowsViewFilters) => {
              return workflowApiService.filterWorkflows({
                ...params,
                attribute: "stage_name",
              });
            },
            apiQueryParam: "search",
            filterIcon: true,
            lazyLoad: true,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: t("DEALS.TABLE_HEADERS.STAGE_NAME", 2),
          onValueChange: (value: string[]) => (filters.stage_names = value),
          loadedMore: (value: StageNameEntry[]) =>
            handleStageNamesOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: StageNameEntry[]) =>
            handleStageNamesOptionsUpdate(value, "search"),
          numberOfFilters: filters.stage_names.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.WORKFLOW_NAME", 1),
        key: "workflow_name",
        class: "min-w-max line-clamp-2",
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[excludableFiltersMap.value.workflowTemplates.activeMode],
            options: workflowTemplates.value,
            placeholder: t("DEALS.TABLE_HEADERS.WORKFLOW_NAME", 1),
            name: "workflow_template_name",
            enableSearch: true,
            showClearFilter: true,
            getPaginatedOptions: (params: WorkflowsViewFilters) => {
              if (isClient) params.global = 1;
              return workflowTemplateService.getWorkflowTemplates(params);
            },
            apiQueryParam: "search",
            isExcludeModeEnabled:
              excludableFiltersMap.value.workflowTemplates.activeMode ===
              NOT_WORKFLOW_TEMPLATE_IDS,
            filterIcon: true,
            lazyLoad: true,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: t("DEALS.TABLE_HEADERS.WORKFLOW_NAME", 2),
          onValueChange: (value: string[]) =>
            (filters[excludableFiltersMap.value.workflowTemplates.activeMode] =
              value),
          loadedMore: (value: IWorkflow[]) =>
            handleWorkflowTemplatesOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IWorkflow[]) =>
            handleWorkflowTemplatesOptionsUpdate(value, "search"),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.workflowTemplates.activeMode = value
              ? NOT_WORKFLOW_TEMPLATE_IDS
              : WORKFLOW_TEMPLATE_IDS;

            handleQueryChange("workflowTemplates");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.workflowTemplates.activeMode]
              .length,
        },
      },
      isLendflowUser
        ? {
            label: t("DEALS.TABLE_HEADERS.ORGANIZATION"),
            key: "client.name",
            class: "min-w-max line-clamp-2",
            filterComponent: {
              initialRender: true, // this component requests data each time its rendered, let it load once and hide
              component: LfCheckboxDropdown,
              props: {
                modelValue:
                  filters[excludableFiltersMap.value.partners.activeMode],
                options: partners.value,
                placeholder: t("DEALS.TABLE_HEADERS.ORGANIZATION"),
                name: "partner",
                enableSearch: true,
                showClearFilter: true,
                getPaginatedOptions: getApprovedClients,
                apiQueryParam: "search",
                isExcludeModeEnabled:
                  excludableFiltersMap.value.partners.activeMode ===
                  NOT_PARTNERS,
                filterIcon: true,
                lazyLoad: true,
              },
              sortBy: filters.sort_by,
              sortByChange,
              plural: t("DEALS.TABLE_HEADERS.ORGANIZATIONS"),
              onValueChange: (value: string[]) =>
                (filters[excludableFiltersMap.value.partners.activeMode] =
                  value),
              loadedMore: (value: IClient[]) =>
                handlePartnersOptionsUpdate(value, "loadmore"),
              optionsSearch: (value: IClient[]) =>
                handlePartnersOptionsUpdate(value, "search"),
              excludeModeChanged: (value: boolean) => {
                excludableFiltersMap.value.partners.activeMode = value
                  ? NOT_PARTNERS
                  : PARTNERS;

                handleQueryChange("partners");
              },
              numberOfFilters:
                filters[excludableFiltersMap.value.partners.activeMode].length,
            },
          }
        : null,
      {
        label: t("DEALS.TABLE_HEADERS.APP_STARTED"),
        key: "created_at",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.created_at_range = [];
          },
          props: {
            modelValue: filters.created_at_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "created_at",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            class: "lf-date-picker",
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.APP_STARTED")}: ${
            filters.created_at_range[0]
          } - ${filters.created_at_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.created_at_range = value),
          hasFiltersSet:
            !!filters.created_at_range[0] && !!filters.created_at_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.SUBMISSION_PARTNER"),
        key: "submission_partner.name",
        component: LfTag,
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.submission_partners,
            options: submissionPartners.value,
            placeholder: t("DEALS.TABLE_HEADERS.SUBMISSION_PARTNER"),
            name: "submission_partners",
            enableSearch: true,
            showClearFilter: true,
            getPaginatedOptions: async (params: WorkflowsViewFilters) => {
              const response = await clientsApiService.getClients({
                ...params,
                basics_only: 1,
              });

              const shouldAppendLendflow =
                !params?.search ||
                "lendflow".includes(params.search?.trim().toLowerCase());

              response.data = [
                shouldAppendLendflow
                  ? { name: "Lendflow", id: "Lendflow" }
                  : undefined,
                ...response.data,
              ].filter(Boolean) as IClient[];

              return response;
            },
            apiQueryParam: "search",
            filterIcon: true,
            lazyLoad: true,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: t("DEALS.TABLE_HEADERS.SUBMISSION_PARTNER"),
          onValueChange: (value: string[]) =>
            (filters.submission_partners = value),
          loadedMore: (value: IClient[]) =>
            handleSubmissionPartnersOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IClient[]) =>
            handleSubmissionPartnersOptionsUpdate(value, "search"),
          numberOfFilters: filters.submission_partners.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.APP_COMPLETED"),
        key: "finished_at",
        component: DateAndTimeDisplay,
        columnClass: "min-w-40",
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.finished_at_range = [];
          },
          props: {
            modelValue: filters.finished_at_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "finished_at",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            class: "lf-date-picker",
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.APP_COMPLETED")}: ${
            filters.finished_at_range[0]
          } - ${filters.finished_at_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.finished_at_range = value),
          hasFiltersSet:
            !!filters.finished_at_range[0] && !!filters.finished_at_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.FUNDED_DATE"),
        key: "accepted_offer.funded_date",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.funded_date_range = [];
          },
          props: {
            modelValue: filters.funded_date_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "funded_date",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.FUNDED_DATE")}: ${
            filters.funded_date_range[0]
          } - ${filters.funded_date_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.funded_date_range = value),
          hasFiltersSet:
            !!filters.funded_date_range[0] && !!filters.funded_date_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.FUNDING_TERM_START_DATE"),
        key: "funding_term_start_date",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.funding_term_start_date_range = [];
          },
          props: {
            modelValue: filters.funding_term_start_date_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "funding_term_start_date_range",
            showOnMount: true,
            disabled: dealsLoading.value,
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.FUNDING_TERM_START_DATE")}: ${
            filters.funding_term_start_date_range[0]
          } - ${filters.funding_term_start_date_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.funding_term_start_date_range = value),
          hasFiltersSet:
            !!filters.funding_term_start_date_range[0] &&
            !!filters.funding_term_start_date_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.FUNDING_TERM_END_DATE"),
        key: "funding_term_end_date",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.funding_term_end_date_range = [];
          },
          props: {
            modelValue: filters.funding_term_end_date_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "funding_term_end_date_range",
            showOnMount: true,
            disabled: dealsLoading.value,
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.FUNDING_TERM_END_DATE")}: ${
            filters.funding_term_end_date_range[0]
          } - ${filters.funding_term_end_date_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.funding_term_end_date_range = value),
          hasFiltersSet:
            !!filters.funding_term_end_date_range[0] &&
            !!filters.funding_term_end_date_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.LAST_CALL"),
        key: "last_call_range",
        component: LastCall,
        formatter: formatDate,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.last_call_range = [];
          },
          props: {
            modelValue: filters.last_call_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "last_call",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            class: "lf-date-picker",
            showTriggerButton: false,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: `${t("DEALS.TABLE_HEADERS.LAST_CALL")}: ${
            filters.last_call_range[0]
          } - ${filters.last_call_range[1]}`,
          onValueChange: (value: string[]) => (filters.last_call_range = value),
          hasFiltersSet:
            !!filters.last_call_range[0] && !!filters.last_call_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.CALL_DISPOSITION"),
        key: "last_call_session.call_disposition",
        formatter: (value: string) =>
          getters["options/callDispositionMap"][value] || "-",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.last_call_disposition,
            options: getters["options/callDispositionMap"],
            name: "last_call_disposition",
            showClearFilter: true,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.CALL_DISPOSITION"),
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.last_call_disposition = value),
          numberOfFilters: filters.last_call_disposition?.length ?? 0,
        },
      },
      {
        label: t("DEALS.SOURCE.TITLE"),
        key: "source",
        formatter: formatDealSource,
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.sources,
            options: availableSources.value,
            name: "source",
            showClearFilter: true,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.SOURCES"),
          onValueChange: (value: string[]) => (filters.sources = value),
          numberOfFilters: filters.sources.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.LATEST_OFFER_CREATED"),
        key: "latest_offer_created_at",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.last_offer_created_range = [];
          },
          props: {
            modelValue: filters.last_offer_created_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "last_offer_created_range",
            showOnMount: true,
            disabled: dealsLoading.value,
            class: "lf-date-picker",
            showTriggerButton: false,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: `${t("DEALS.TABLE_HEADERS.LATEST_OFFER_CREATED")}: ${
            filters.last_offer_created_range[0]
          } - ${filters.last_offer_created_range[1]}`,
          onValueChange: (value: string[]) =>
            (filters.last_offer_created_range = value),
          hasFiltersSet:
            !!filters.last_offer_created_range[0] &&
            !!filters.last_offer_created_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.WORKFLOW_CATEGORY"),
        key: "workflow",
        formatter: (val: number) => {
          return workflowCategoriesDropdownOptions.value[val];
        },
        filterComponent: {
          initialRender: false,
          component: "lf-checkbox-dropdown",
          props: {
            modelValue: filters.workflow_categories || [],
            options: workflowCategoriesDropdownOptions.value,
            name: "workflow_categories",
            showClearFilter: true,
            filterIcon: true,
            sort: true,
          },
          onValueChange: (value: number[]) =>
            (filters.workflow_categories = value),
          plural: t("DEALS.TABLE_HEADERS.WORKFLOW_CATEGORIES"),
          numberOfFilters: filters.workflow_categories.length,
        },
      },
      {
        label: `${t("COMMON.LEGACY")} ${t("DEALS.TABLE_HEADERS.WORKFLOW_CATEGORY")}`,
        key: "is_business_credit",
        component: DealWorkflowField,
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: appliedWorkflows.value,
            options: WORKFLOWS,
            name: "workflows",
            showClearFilter: true,
            filterIcon: true,
            sort: true,
          },
          plural: `${t("COMMON.LEGACY")} ${t("DEALS.TABLE_HEADERS.WORKFLOW_CATEGORIES")}`,
          onValueChange: (value: string[]) => {
            appliedWorkflows.value = value;
            const allWorkflows = Object.keys(WORKFLOWS) as Array<
              keyof typeof WORKFLOWS
            >;
            // Create a Set for O(1) lookup instead of O(n) includes for each workflow
            const valueSet = new Set(value);
            allWorkflows.forEach((workflow) => {
              filters[workflow] = valueSet.has(workflow) ? 1 : 0;
            });
          },
          numberOfFilters: appliedWorkflows.value.length,
        },
      },
      ...(isLendflowTable.value
        ? [
            {
              label: t("DEALS.TABLE_HEADERS.FUNDING_ADVISOR"),
              key: "client_advisor_full_name",
              component: AssignUserField,
              props: {
                type: "clientFundingAdvisor",
              },
              filterComponent: {
                component: FilterByUser,
                props: {
                  modelValue: filters.client_funding_advisor_ids,
                  name: CLIENT_FUNDING_ADVISOR,
                  enableSearch: true,
                  showClearFilter: true,
                  filterIcon: true,
                },
                plural: t("DEALS.TABLE_HEADERS.FUNDING_ADVISORS"),
                onValueChange: (value: number[]) =>
                  (filters.client_funding_advisor_ids = value),
                numberOfFilters: filters.client_funding_advisor_ids.length,
              },
            },
            {
              label: t("DEALS.TABLE_HEADERS.UNDERWRITER"),
              key: "client_underwriter_full_name",
              component: AssignUserField,
              props: {
                type: "clientUnderwriter",
              },
              filterComponent: {
                component: FilterByUser,
                props: {
                  modelValue: filters.client_underwriter_ids,
                  name: CLIENT_UNDERWRITER,
                  enableSearch: true,
                  showClearFilter: true,
                  filterIcon: true,
                },
                plural: t("DEALS.TABLE_HEADERS.UNDERWRITERS"),
                onValueChange: (value: number[]) =>
                  (filters.client_underwriter_ids = value),
                numberOfFilters: filters.client_underwriter_ids.length,
              },
            },
            {
              label: t("DEALS.TABLE_HEADERS.ANALYST"),
              key: "client_analyst_full_name",
              component: AssignUserField,
              props: {
                type: "clientAnalyst",
              },
              filterComponent: {
                component: FilterByUser,
                props: {
                  modelValue: filters.client_analyst_ids,
                  name: CLIENT_ANALYST,
                  enableSearch: true,
                  showClearFilter: true,
                  filterIcon: true,
                },
                plural: t("DEALS.TABLE_HEADERS.ANALYSTS"),
                onValueChange: (value: number[]) =>
                  (filters.client_analyst_ids = value),
                numberOfFilters: filters.client_analyst_ids.length,
              },
            },
            {
              label: t("DEALS.TABLE_HEADERS.TYPE"),
              key: "is_legacy",
              formatter: formatDealType,
              filterComponent: {
                initialRender: false,
                component: "lf-dropdown",
                props: {
                  modelValue: filters.is_legacy,
                  options: PRODUCT_TYPES,
                  name: "is_legacy",
                  filterIcon: true,
                  showClearFilter: true,
                },
                plural:
                  PRODUCT_TYPES[
                    filters.is_legacy as keyof typeof PRODUCT_TYPES
                  ],
                onChange: (value: string) => (filters.is_legacy = value),
                hasFiltersSet: Object.keys(PRODUCT_TYPES).includes(
                  filters.is_legacy.toString()
                ),
              },
            },
            isLendflowUser
              ? {
                  label: t("DEALS.TABLE_HEADERS.LENDFLOW_FUNDING_ADVISOR"),
                  key: "advisor_full_name",
                  component: AssignUserField,
                  props: {
                    type: "fundingAdvisor",
                  },
                  filterComponent: {
                    component: FilterByUser,
                    props: {
                      modelValue:
                        filters[
                          excludableFiltersMap.value.fundingAdvisorIds
                            .activeMode
                        ],
                      name: FUNDING_ADVISOR,
                      enableSearch: true,
                      isExcludeModeEnabled:
                        excludableFiltersMap.value.fundingAdvisorIds
                          .activeMode === NOT_FUNDING_ADVISOR_IDS,
                      showClearFilter: true,
                      filterIcon: true,
                    },
                    plural: t("DEALS.TABLE_HEADERS.FUNDING_ADVISORS"),
                    onValueChange: (value: string[]) =>
                      (filters[
                        excludableFiltersMap.value.fundingAdvisorIds.activeMode
                      ] = value),
                    numberOfFilters:
                      filters[
                        excludableFiltersMap.value.fundingAdvisorIds.activeMode
                      ].length,
                    excludeModeChanged: (value: boolean) => {
                      excludableFiltersMap.value.fundingAdvisorIds.activeMode =
                        value ? NOT_FUNDING_ADVISOR_IDS : FUNDING_ADVISOR_IDS;

                      handleQueryChange("fundingAdvisorIds");
                    },
                  },
                }
              : null,
            isLendflowUser
              ? {
                  label: t("DEALS.TABLE_HEADERS.LENDFLOW_UNDERWRITER"),
                  key: "underwriter_full_name",
                  component: !(isFundingAdvisor || isClientFundingAdvisor)
                    ? AssignUserField
                    : null,
                  props: {
                    type: "underwriter",
                  },
                  filterComponent: !(isFundingAdvisor || isClientFundingAdvisor)
                    ? {
                        component: FilterByUser,
                        props: {
                          modelValue:
                            filters[
                              excludableFiltersMap.value.underwriterIds
                                .activeMode
                            ],
                          name: UNDERWRITER,
                          enableSearch: true,
                          isExcludeModeEnabled:
                            excludableFiltersMap.value.underwriterIds
                              .activeMode === NOT_UNDERWRITER_IDS,
                          showClearFilter: true,
                          filterIcon: true,
                        },
                        plural: t("DEALS.TABLE_HEADERS.UNDERWRITERS"),
                        onValueChange: (value: string[]) =>
                          (filters[
                            excludableFiltersMap.value.underwriterIds.activeMode
                          ] = value),
                        excludeModeChanged: (value: boolean) => {
                          excludableFiltersMap.value.underwriterIds.activeMode =
                            value ? NOT_UNDERWRITER_IDS : UNDERWRITER_IDS;

                          handleQueryChange("underwriterIds");
                        },
                        numberOfFilters:
                          filters[
                            excludableFiltersMap.value.underwriterIds.activeMode
                          ].length,
                      }
                    : null,
                }
              : null,
            isLendflowUser
              ? {
                  label: t("DEALS.TABLE_HEADERS.LENDFLOW_ANALYST"),
                  key: "analyst_full_name",
                  component: canAssignAnalyst.value ? AssignUserField : null,
                  props: {
                    type: "analyst",
                  },
                  filterComponent: canAssignAnalyst.value
                    ? {
                        component: FilterByUser,
                        props: {
                          modelValue:
                            filters[
                              excludableFiltersMap.value.analystIds.activeMode
                            ],
                          name: ANALYST,
                          enableSearch: true,
                          isExcludeModeEnabled:
                            excludableFiltersMap.value.analystIds.activeMode ===
                            NOT_ANALYST_IDS,
                          showClearFilter: true,
                          filterIcon: true,
                        },
                        plural: t("DEALS.TABLE_HEADERS.LENDFLOW_ANALYST"),
                        onValueChange: (value: number[]) =>
                          (filters[
                            excludableFiltersMap.value.analystIds.activeMode
                          ] = value),
                        excludeModeChanged: (value: boolean) => {
                          excludableFiltersMap.value.analystIds.activeMode =
                            value ? NOT_ANALYST_IDS : ANALYST_IDS;

                          handleQueryChange("analystIds");
                        },
                        numberOfFilters:
                          filters[
                            excludableFiltersMap.value.analystIds.activeMode
                          ].length,
                      }
                    : null,
                }
              : null,
          ]
        : [
            {
              label: t("DEALS.TABLE_HEADERS.BUSINESS_OWNER"),
              key: "personal_information",
              formatter: formatUserFullName,
            },
          ]),
      {
        label: t("DEALS.TABLE_HEADERS.LENDFLOW_UUID"),
        key: "id",
        component: ClickToCopyIdColumn,
        formatter: (value: string) => truncateWithEllipsis(value, 6),
      },
      {
        label: t("DEALS.TABLE_HEADERS.CLIENT_UUID"),
        class: "w-46 break-all",
        key: "client_tracking_token",
        component: ClickToCopyIdColumn,
        formatModel: true,
        formatter: (value: IApplication) =>
          value.client_tracking_token
            ? truncateWithEllipsis(value.client_tracking_token, 6)
            : "",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[
                excludableFiltersMap.value.clientTrackingTokens.activeMode
              ],
            options: clientTrackingTokens.value,
            name: "client_tracking_tokens",
            enableSearch: true,
            showClearFilter: true,
            filterIcon: true,
            valueIsOption: true,
            apiQueryParam: "search",
            isExcludeModeEnabled:
              excludableFiltersMap.value.clientTrackingTokens.activeMode ===
              NOT_CLIENT_TRACKING_TOKENS,
            getPaginatedOptions: clientsApiService.getAllClientsTrackingToken,
          },
          plural: t("DEALS.TABLE_HEADERS.CLIENT_UUID"),
          onValueChange: (value: string[]) =>
            (filters[
              excludableFiltersMap.value.clientTrackingTokens.activeMode
            ] = value),
          loadedMore: (value: string[]) =>
            handleClientTokensUpdate(value, "loadmore"),
          optionsSearch: (value: string[]) =>
            handleClientTokensUpdate(value, "search"),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.clientTrackingTokens.activeMode = value
              ? NOT_CLIENT_TRACKING_TOKENS
              : CLIENT_TRACKING_TOKENS;

            handleQueryChange("clientTrackingTokens");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.clientTrackingTokens.activeMode]
              .length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.ADDITIONAL_TRACKING_TOKENS"),
        class: "w-46",
        key: "latest_tracking_token",
        component: AdditionalTokenDisplay,
        columnClass: "max-w-46",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[
                excludableFiltersMap.value.additionalTrackingTokens.activeMode
              ],
            options: additionalTrackingTokens.value,
            name: "tracking_tokens",
            enableSearch: true,
            showClearFilter: true,
            filterIcon: true,
            valueIsOption: true,
            apiQueryParam: "search",
            isExcludeModeEnabled:
              excludableFiltersMap.value.additionalTrackingTokens.activeMode ===
              NOT_ADDITIONAL_TRACKING_TOKENS,
            getPaginatedOptions: clientsApiService.getAdditionalTrackingTokens,
          },
          plural: t("DEALS.TABLE_HEADERS.ADDITIONAL_TRACKING_TOKENS"),
          onValueChange: (value: string[]) =>
            (filters[
              excludableFiltersMap.value.additionalTrackingTokens.activeMode
            ] = value),
          loadedMore: (value: { token: string }[]) =>
            handleAdditionalTokensUpdate(value, "loadmore"),
          optionsSearch: (value: { token: string }[]) =>
            handleAdditionalTokensUpdate(value, "search"),
          sortBy: filters.sort_by,
          sortByChange: () => sortByChange("additional_tracking_tokens"),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.additionalTrackingTokens.activeMode =
              value
                ? NOT_ADDITIONAL_TRACKING_TOKENS
                : ADDITIONAL_TRACKING_TOKENS;

            handleQueryChange("additionalTrackingTokens");
          },
          numberOfFilters:
            filters[
              excludableFiltersMap.value.additionalTrackingTokens.activeMode
            ].length,
        },
      },
      {
        label: t("DEALS.TRACKING.REFERRER_URL"),
        key: "referrer_absolute_url",
        component: ReferrerHostDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-checkbox-dropdown",
          props: {
            modelValue: filters.referrer_host,
            options: referrerHosts.value,
            name: "referrer_host",
            enableSearch: true,
            showClearFilter: true,
            filterIcon: true,
            valueIsOption: true,
            apiQueryParam: "search",
            getPaginatedOptions:
              applicationsApiService.getReferrerHostParameters,
          },
          plural: t("DEALS.TRACKING.REFERRER_URL"),
          onValueChange: (value: string[]) => (filters.referrer_host = value),
          loadedMore: (value: { host_name: string }[]) =>
            handleReferrerHostsOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: { host_name: string }[]) =>
            handleReferrerHostsOptionsUpdate(value, "search"),
          numberOfFilters: filters.referrer_host.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.UTM_PARAMETERS"),
        class: "w-46 break-words",
        key: "utm_parameters",
        component: UtmParameters,
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.utm_parameters,
            options: utmParameters.value,
            name: "utm_parameters",
            enableSearch: true,
            showClearFilter: true,
            filterIcon: true,
            valueIsOption: true,
            apiQueryParam: "search",
            getPaginatedOptions: clientsApiService.getUtmParameters,
          },
          plural: t("DEALS.TABLE_HEADERS.UTM_PARAMETERS"),
          onValueChange: (value: string[]) => (filters.utm_parameters = value),
          loadedMore: (value: string[]) =>
            handleUtmParamsUpdate(value, "loadmore"),
          optionsSearch: (value: string[]) =>
            handleUtmParamsUpdate(value, "search"),
          numberOfFilters: filters.utm_parameters.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.GENERAL_INDUSTRY"),
        key: "business.industry_type_id",
        class: "w-46 line-clamp-2",
        formatter: formatIndustryTypes,
        filterComponent: {
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.industry_types,
            options: industryTypesOptions.value,
            name: "industry_types",
            placeholder: t("DEALS.TABLE_HEADERS.GENERAL_INDUSTRY"),
            enableSearch: true,
            showClearFilter: true,
            filterIcon: true,
            class: "min-w-64",
            dropdownWidthFull: true,
          },
          plural: t("DEALS.TABLE_HEADERS.GENERAL_INDUSTRY"),
          onValueChange: (value: number[]) => (filters.industry_types = value),
          numberOfFilters: filters.industry_types?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.SUB_INDUSTRY"),
        key: "industry",
        component: SubIndustryCell,
      },
      {
        label: t("DEALS.TABLE_HEADERS.INDUSTRY_OTHER"),
        key: "business.industry_other",
        class: "w-46 line-clamp-2",
      },
      {
        label: t("DEALS.TABLE_HEADERS.STATE"),
        key: "business.address.state",
        formatter: formatStateName,
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.states,
            options: states.value,
            placeholder: t("DEALS.TABLE_HEADERS.STATE"),
            name: "states",
            showClearFilter: true,
            filterIcon: true,
            valueIsOption: true,
          },
          plural: t("DEALS.TABLE_HEADERS.STATE"),
          onValueChange: (value: string[]) => (filters.states = value),
          numberOfFilters: filters.states.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.CAPITAL_USE_CASE"),
        key: "use_of_fund_id",
        formatter: formatUseOfFunds,
      },
      {
        label: t("DEALS.TABLE_HEADERS.WHEN_FUNDS_NEEDED"),
        key: "funds_needed_time",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.funds_needed_time || [],
            options: fundsNeededTimesOptions.value,
            name: "funds_needed_time",
            placeholder: t("DEALS.TABLE_HEADERS.WHEN_FUNDS_NEEDED"),
            showClearFilter: true,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.WHEN_FUNDS_NEEDED"),
          onValueChange: (value: string[]) =>
            (filters.funds_needed_time = value),
          numberOfFilters: filters.funds_needed_time?.length || 0,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.FUNDING_AMOUNT_REQUESTED"),
        key: "amount_needed",
        class: "text-right",
        formatModel: true,
        formatter: (val: IApplication) =>
          moneyToTwoDecimal(val.amount_needed ?? 0),
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.amount_needed_range,
            name: "amount_needed_range",
            onChange: (value: number[]) => {
              filters.amount_needed_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.FUNDING_AMOUNT_REQUESTED"),
            unit: Unit.Dollar,
            mask: "#########",
            subTitle: t("COMMON.DATES.LAST_MONTH"),
          },
          plural: t("DEALS.TABLE_HEADERS.FUNDING_AMOUNT_REQUESTED"),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.amount_needed_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.ACTUAL_MONTHLY_REVENUE"),
        key: "business.actual_monthly_revenue",
        class: "text-right",
        formatModel: true,
        formatter: (val: IApplication) =>
          moneyToTwoDecimal(val.business?.actual_monthly_revenue ?? 0),
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.actual_monthly_revenue_range,
            name: "actual_monthly_revenue_range",
            onChange: (value: number[]) => {
              filters.actual_monthly_revenue_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.ACTUAL_MONTHLY_REVENUE"),
            unit: Unit.Dollar,
            mask: "#########",
            subTitle: t("COMMON.DATES.LAST_MONTH"),
          },
          plural: t("DEALS.TABLE_HEADERS.ACTUAL_MONTHLY_REVENUE"),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.actual_monthly_revenue_range?.length,
        },
      },
      {
        label: `${t("COMMON.WORKFLOW")} ${t("COMMON.STATUS")}`,
        key: "workflow_status.status",
        component: WorkflowStatusSelector,
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[excludableFiltersMap.value.workflowStatuses.activeMode],
            options: workflowStatuses.value,
            placeholder: `${t("COMMON.WORKFLOW")} ${t("COMMON.STATUS")}`,
            name: "workflow_statuses",
            enableSearch: true,
            showClearFilter: true,
            getPaginatedOptions: (params: WorkflowsViewFilters) => {
              return workflowApiService.filterWorkflows({
                ...params,
                attribute: "status",
              });
            },
            apiQueryParam: "search",
            isExcludeModeEnabled:
              excludableFiltersMap.value.workflowStatuses.activeMode ===
              NOT_WORKFLOW_STATUSES,
            filterIcon: true,
            lazyLoad: true,
            sort: true,
            sortingFunction: stringCompare,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: `${t("COMMON.WORKFLOW")} ${t("COMMON.STATUS", 2)}`,
          onValueChange: (value: string[]) =>
            (filters[excludableFiltersMap.value.workflowStatuses.activeMode] =
              value),
          loadedMore: (value: WorkflowSnapshotStatus[]) => {
            handleWorkflowStatusesOptionsUpdate(value, "loadmore");
          },
          optionsSearch: (value: WorkflowSnapshotStatus[]) =>
            handleWorkflowStatusesOptionsUpdate(value, "search"),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.workflowStatuses.activeMode = value
              ? NOT_WORKFLOW_STATUSES
              : WORKFLOW_STATUSES;

            handleQueryChange("workflowStatuses");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.workflowStatuses.activeMode]
              .length,
        },
      },
      {
        label: `${t("COMMON.WORKFLOW")} ${t("COMMON.SUB_STATUS")}`,
        key: "workflow_status.sub_status",
        component: WorkflowStatusSelector,
        props: {
          type: "sub_status",
        },
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[
                excludableFiltersMap.value.workflowSubStatuses.activeMode
              ],
            options: workflowSubStatuses.value,
            placeholder: `${t("COMMON.WORKFLOW")} ${t("COMMON.SUB_STATUS")}`,
            name: "workflow_sub_statuses",
            enableSearch: true,
            showClearFilter: true,
            getPaginatedOptions: (params: WorkflowsViewFilters) => {
              return workflowApiService.filterWorkflows({
                ...params,
                attribute: "sub_status",
              });
            },
            apiQueryParam: "search",
            isExcludeModeEnabled:
              excludableFiltersMap.value.workflowSubStatuses.activeMode ===
              NOT_WORKFLOW_SUB_STATUSES,
            filterIcon: true,
            lazyLoad: true,
            sort: true,
            sortingFunction: stringCompare,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: `${t("COMMON.WORKFLOW")} ${t("COMMON.SUB_STATUS", 2)}`,
          onValueChange: (value: string[]) =>
            (filters[
              excludableFiltersMap.value.workflowSubStatuses.activeMode
            ] = value),
          loadedMore: (value: WorkflowSnapshotStatus[]) =>
            handleWorkflowSubStatusesOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: WorkflowSnapshotStatus[]) =>
            handleWorkflowSubStatusesOptionsUpdate(value, "search"),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.workflowSubStatuses.activeMode = value
              ? NOT_WORKFLOW_SUB_STATUSES
              : WORKFLOW_SUB_STATUSES;

            handleQueryChange("workflowSubStatuses");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.workflowSubStatuses.activeMode]
              .length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.DEAD_DESCRIPTION"),
        key: "death.notes",
      },
      {
        label: t("DEALS.TABLE_HEADERS.SUBMITTED_TO_UNDERWRITING"),
        key: "submitted_to_underwriting",
        component: CheckmarkCell,
        props: {
          resolve: (row: IApplication) =>
            (row?.status || 0) >= STATUS_UNDERWRITING,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.PLACEMENT_STAGE"),
        key: "placement_status",
        component: CheckmarkCell,
        props: {
          resolve: (row: IApplication) =>
            (row?.status || 0) >= STATUS_PLACEMENT,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.SOFTWARE_USER"),
        key: "software_user",
        component: CheckmarkCell,
        props: {
          resolve: (row: IApplication) =>
            row?.business?.business_management_software || false,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.FUNDED_AMOUNT"),
        key: "funded_amount",
        class: "text-right",
        formatModel: true,
        formatter: (val: IApplication) =>
          moneyToTwoDecimal(val.funded_amount ?? 0),
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.funded_amount_range,
            name: "funded_amount_range",
            onChange: (value: number[]) => {
              filters.funded_amount_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.FUNDED_AMOUNT"),
            unit: Unit.Dollar,
            mask: "#########",
          },
          plural: t("DEALS.TABLE_HEADERS.FUNDED_AMOUNT"),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.funded_amount_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.ORIGIN_PARTNER"),
        key: "origin_client.name",
        class: "min-w-max line-clamp-2",
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[excludableFiltersMap.value.originPartners.activeMode],
            options: originPartners.value,
            placeholder: t("DEALS.TABLE_HEADERS.ORIGIN_PARTNER"),
            name: "origin_partners",
            enableSearch: true,
            showClearFilter: true,
            getPaginatedOptions: getApprovedClients,
            apiQueryParam: "search",
            isExcludeModeEnabled:
              excludableFiltersMap.value.originPartners.activeMode ===
              NOT_ORIGIN_PARTNERS,
            filterIcon: true,
            lazyLoad: true,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: t("DEALS.TABLE_HEADERS.ORIGIN_PARTNER"),
          onValueChange: (value: string[]) =>
            (filters[excludableFiltersMap.value.originPartners.activeMode] =
              value),
          loadedMore: (value: IClient[]) =>
            handleOriginPartnersOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IClient[]) =>
            handleOriginPartnersOptionsUpdate(value, "search"),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.originPartners.activeMode = value
              ? NOT_ORIGIN_PARTNERS
              : ORIGIN_PARTNERS;
            handleQueryChange("originPartners");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.originPartners.activeMode]
              ?.length || 0,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.STATED_MONTHLY_REVENUE"),
        class: "whitespace-nowrap text-right",
        key: "stated_monthly_revenue",
        formatter: (val: keyof typeof revenueRanges.value) =>
          revenueRanges.value[val],
        placeholder: moneyToTwoDecimal(0),
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.stated_monthly_revenue,
            options: revenueRanges.value,
            name: "stated_monthly_revenue",
            placeholder: t("DEALS.TABLE_HEADERS.STATED_MONTHLY_REVENUE"),
            showClearFilter: true,
            filterIcon: true,
          },
          onValueChange: (value: string[]) =>
            (filters.stated_monthly_revenue = value),
          hasFiltersSet: !!filters.stated_monthly_revenue.length,
          plural: t("DEALS.TABLE_HEADERS.STATED_MONTHLY_REVENUE"),
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.PLACED_WITH_FUNDER"),
        component: CheckmarkCell,
        key: "placed_with_funder",
        props: {
          resolve: (row: IApplication) => row?.placements_count > 0,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.SIGNED_APPLICATION"),
        component: CheckmarkCell,
        key: "is_signed",
        props: {
          resolve: (row: IApplication) => row?.is_signed || false,
        },
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.is_signed,
            options: YES_NO_OPTIONS,
            name: "is_signed",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.SIGNED_APPLICATION")}-${
            filterHasValue(filters.is_signed)
              ? YES_NO_OPTIONS[filters.is_signed as keyof typeof YES_NO_OPTIONS]
              : ""
          }`,
          onChange: (value: number) => (filters.is_signed = value),
          hasFiltersSet: filterHasValue(filters.is_signed),
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.PLAID_CONNECTION"),
        component: CheckmarkCell,
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.has_plaid_connection,
            options: YES_NO_OPTIONS,
            name: "plaid_connection",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.PLAID_CONNECTION")}-${
            filterHasValue(filters.has_plaid_connection)
              ? YES_NO_OPTIONS[
                  filters.has_plaid_connection as keyof typeof YES_NO_OPTIONS
                ]
              : ""
          }`,
          onChange: (value: number) => (filters.has_plaid_connection = value),
          hasFiltersSet: filterHasValue(filters.has_plaid_connection),
        },
        key: "plaid_connection",
        props: {
          resolve: (row: IApplication) => row?.plaid_connections_count > 0,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.DISCOVERY"),
        component: DiscoverStatusDropdown,
        props: {
          disabled: !canUpdateDiscoveryStatus.value,
        },
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.is_discovery_complete,
            options: DISCOVERY_FILTER_OPTIONS,
            name: "discovery_status",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.DISCOVERY")}-${
            filterHasValue(filters.is_discovery_complete)
              ? DISCOVERY_FILTER_OPTIONS[
                  filters.is_discovery_complete as keyof typeof DISCOVERY_FILTER_OPTIONS
                ]
              : ""
          }`,
          onChange: (value: number) => (filters.is_discovery_complete = value),
          hasFiltersSet: filterHasValue(filters.is_discovery_complete),
        },
        key: "is_discovery_complete",
      },
      {
        label: t("DEALS.TABLE_HEADERS.TIME_IN_CURRENT_STAGE"),
        key: "time_in_current_stage",
        formatter: formatTimeElapsed,
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.time_in_current_stage_range,
            name: "time_in_current_stage",
            onChange: (value: number[]) => {
              filters.time_in_current_stage_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            unit: Unit.Hours,
            label: t("DEALS.TABLE_HEADERS.TIME_IN_CURRENT_STAGE"),
          },
          plural: t("DEALS.TABLE_HEADERS.TIME_IN_CURRENT_STAGE"),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.time_in_current_stage_range?.length,
        },
      },
      {
        label: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.NAME")}`,
        key: "score_card_name",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.score_card_ids,
            name: "score_card_name",
            options: scorecardsOptions.value,
            filterIcon: true,
            showClearFilter: true,
            enableSearch: true,
            placeholder: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.NAME")}`,
            apiQueryParam: "search",
            getPaginatedOptions: scorecardsApiService.getPublishedScorecards,
          },
          onValueChange: (value: string[]) => {
            filters.score_card_ids = value;
          },
          loadedMore: (value: IScorecard[]) =>
            handleScorecardsOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IScorecard[]) =>
            handleScorecardsOptionsUpdate(value, "search"),
          plural: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.NAME")}`,
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.score_card_ids?.length,
        },
      },
      {
        label: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD_GROUP_NAME")}`,
        key: "score_card_group_name",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.score_card_group_ids,
            name: "score_card_group_name",
            options: scorecardGroupsOptions.value,
            filterIcon: true,
            showClearFilter: true,
            enableSearch: true,
            placeholder: `${t("COMMON.PRIMARY")} ${t(
              "SCORECARDS.SCORECARD_GROUP_NAME"
            )}`,
            apiQueryParam: "search",
            getPaginatedOptions:
              scorecardsApiService.getPublishedScorecardGroups,
          },
          onValueChange: (value: string[]) => {
            filters.score_card_group_ids = value;
          },
          loadedMore: (value: IScorecardGroup[]) =>
            handleScorecardGroupsOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IScorecardGroup[]) =>
            handleScorecardGroupsOptionsUpdate(value, "search"),
          plural: `${t("COMMON.PRIMARY")} ${t(
            "SCORECARDS.SCORECARD_GROUP_NAME"
          )}`,
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.score_card_group_ids?.length,
        },
      },
      {
        label: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD")} ${t(
          "COMMON.SCORE"
        )}`,
        key: "score_card_result.points",
        class: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            unit: Unit.Points,
            modelValue: filters.score_card_points_range,
            name: "score_card_points",
            onChange: (value: number[]) => {
              filters.score_card_points_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD")} ${t(
              "COMMON.SCORE"
            )}`,
          },
          plural: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD")} ${t(
            "COMMON.SCORE"
          )}`,
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.score_card_points_range?.length,
        },
      },
      {
        label: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD_GROUP")} ${t(
          "COMMON.SCORE"
        )}`,
        key: "score_card_group_result.points",
        class: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            unit: Unit.Points,
            modelValue: filters.score_card_group_points_range,
            name: "score_card_group_points",
            onChange: (value: number[]) => {
              filters.score_card_group_points_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: `${t("COMMON.PRIMARY")} ${t(
              "SCORECARDS.SCORECARD_GROUP"
            )} ${t("COMMON.SCORE")}`,
          },
          plural: `${t("COMMON.PRIMARY")} ${t(
            "SCORECARDS.SCORECARD_GROUP"
          )} ${t("COMMON.SCORE")}`,
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.score_card_group_points_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TIME_TO_OFFER_CREATION"),
        key: "time_to_first_offer",
        formatter: formatTimeElapsed,
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.time_to_first_offer_range,
            name: "time_to_first_offer",
            onChange: (value: number[]) => {
              filters.time_to_first_offer_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.TIME_TO_OFFER_CREATION"),
            unit: Unit.Minutes,
          },
          plural: t("DEALS.TABLE_HEADERS.TIME_TO_OFFER_CREATION"),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.time_to_first_offer_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.NUMBER_OF_PLACEMENTS"),
        key: "placements_count",
        columnClass: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.placements_count_range,
            name: "placements_count_range",
            onChange: (value: number[]) => {
              filters.placements_count_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.NUMBER_OF_PLACEMENTS"),
          },
          plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_PLACEMENTS"),
          sortBy: filters.sort_by,
          sortByChange,
          hasFiltersSet: !!filters.placements_count_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.HAS_PLACEMENTS"),
        key: "has_placement",
        component: HasDataDisplay,
        props: {
          type: EnrichmentData.placement,
          onEnrichmentDataLoaded: savePlacementData,
        },
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.has_placement,
            options: YES_NO_OPTIONS,
            name: "has_placement",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.HAS_PLACEMENTS")}-${
            filterHasValue(filters.has_placement)
              ? YES_NO_OPTIONS[
                  filters.has_placement as keyof typeof YES_NO_OPTIONS
                ]
              : ""
          }`,
          onChange: (value: number) => (filters.has_placement = value),
          hasFiltersSet: filterHasValue(filters.has_placement),
          sortBy: filters.sort_by,
          sortByChange,
        },
        isSubColumnLoading: subColumnLoading.value.has_placement_loading,
        subColumns: subColumnsVisibility.value.has_placement
          ? [
              {
                label: t("DEALS.SUBCOLUMNS.FUNDER"),
                key: "placementData.funder",
                component: NameList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.PRODUCT_TYPE"),
                key: "placementData.product_type",
                component: ProductTypeList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.SUBMISSION"),
                key: "placementData.submission",
                component: SubmissionList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.RISK_TIER"),
                key: "placementData.risk_tier",
                component: RiskTierList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.PLACEMENT_STATUS"),
                key: "placementData.placement_status",
                component: PlacementStatusList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.DECLINE_REASON"),
                key: "placementData.dead_reason",
                component: DeadReasonList,
              },
              {
                label: t("DEALS.TABLE_HEADERS.TIME_SINCE_FIRST_SUBMISSION"),
                key: "time_since_first_placement",
                formatter: formatTimeElapsed,
              },
              {
                label: t("DEALS.TABLE_HEADERS.TIME_SINCE_LAST_SUBMISSION"),
                key: "time_since_latest_placement",
                formatter: formatTimeElapsed,
              },
              {
                label: t("DEALS.TABLE_HEADERS.NUMBER_OF_OFFERS_DECLINED"),
                key: "offers_declined_count",
              },
              {
                label: `${t("PLACEMENT.TITLE")} ${t("COMMON.WORKFLOW")} ${t(
                  "COMMON.STATUS"
                )}`,
                key: "placementData.workflow_status",
                component: WorkflowStatusList,
                props: { statusType: "status" },
              },
              {
                label: `${t("PLACEMENT.TITLE")} ${t("COMMON.WORKFLOW")} ${t(
                  "COMMON.SUB_STATUS"
                )}`,
                key: "placementData.workflow_status",
                component: WorkflowStatusList,
                props: { statusType: "sub_status" },
              },
            ]
          : [],
      },
      {
        label: t("DEALS.TABLE_HEADERS.NUMBER_OF_OFFERS"),
        key: "offer_count",
        columnClass: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.offers_count_range,
            name: "offers_count_range",
            onChange: (value: number[]) => {
              filters.offers_count_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.NUMBER_OF_OFFERS"),
          },
          plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_OFFERS"),
          sortBy: filters.sort_by,
          sortByChange,
          hasFiltersSet: !!filters.offers_count_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.HAS_OFFERS"),
        key: "has_offer",
        component: HasDataDisplay,
        props: {
          type: EnrichmentData.offer,
          onEnrichmentDataLoaded: saveOfferData,
        },
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.has_offer,
            options: YES_NO_OPTIONS,
            name: "has_offer",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.HAS_OFFERS")}-${
            filterHasValue(filters.has_offer)
              ? YES_NO_OPTIONS[filters.has_offer as keyof typeof YES_NO_OPTIONS]
              : ""
          }`,
          onChange: (value: number) => (filters.has_offer = value),
          hasFiltersSet: filterHasValue(filters.has_offer),
          sortBy: filters.sort_by,
          sortByChange,
        },
        isSubColumnLoading: subColumnLoading.value.has_offer_loading,
        subColumns: subColumnsVisibility.value.has_offer
          ? [
              {
                label: t("DEALS.SUBCOLUMNS.FUNDER"),
                key: "offersData.funder",
                component: NameList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.PRODUCT_TYPE"),
                key: "offersData.product_type",
                component: ProductTypeList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.SUBMISSION_DATE"),
                key: "offersData.submission_date",
                component: DatesList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.APPLICATION_DATE"),
                key: "offersData.application_date",
                component: DatesList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.FUNDED_DATE"),
                key: "offersData.funded_date",
                component: DatesList,
              },
            ]
          : [],
      },
      {
        label: t("DEALS.TABLE_HEADERS.SCORECARD_STATUS"),
        key: "score_card_result.score_card_status",
        component: ScorecardStatusCell,
        props: {
          tableCell: true,
          fontSize: "text-xxs",
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.APP_STATUS"),
        key: "progress",
        component: "lf-pie-chart",
      },
      {
        label: t("DEALS.TABLE_HEADERS.BANK_STATEMENTS"),
        component: CheckmarkCell,
        key: "bank_statements_count",
        props: {
          resolve: (row: IApplication) => row.bank_statements_count > 0,
        },
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.has_bank_statements,
            options: YES_NO_OPTIONS,
            name: "has_bank_statements",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.BANK_STATEMENTS")}-${
            filterHasValue(filters.has_bank_statements)
              ? YES_NO_OPTIONS[
                  filters.has_bank_statements as keyof typeof YES_NO_OPTIONS
                ]
              : ""
          }`,
          onChange: (value: number) => (filters.has_bank_statements = value),
          hasFiltersSet: filterHasValue(filters.has_bank_statements),
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.BANK_DATA"),
        component: CheckmarkCell,
        key: "has_bank_data",
        props: {
          resolve: (row: IApplication) => row.has_bank_data,
        },
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.has_bank_data,
            options: YES_NO_OPTIONS,
            name: "has_bank_data",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.BANK_DATA")}-${
            filterHasValue(filters.has_bank_data)
              ? YES_NO_OPTIONS[
                  filters.has_bank_data as keyof typeof YES_NO_OPTIONS
                ]
              : ""
          }`,
          onChange: (value: number) => (filters.has_bank_data = value),
          hasFiltersSet: filterHasValue(filters.has_bank_data),
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.STAGE_CONTROL"),
        component: StageManagementCell,
        key: "stage_management",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.stage_management,
            options: STAGE_MANAGEMENT_OPTIONS,
            name: "stage_management",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.STAGE_CONTROL")}`,
          onValueChange: (value: number[]): void => {
            filters.stage_management = value;
          },
          numberOfFilters: filters.stage_management.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.CONTACT"),
        component: ContactItem,
        key: "personal_information.telephone",
        columnClass: "min-w-63",
      },
      {
        label: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMPLOYEES"),
        key: "business.number_of_employees_id",
        formatter: (id: number) => {
          const index = numberOfEmployeeIndexById(id);
          return index !== null
            ? numberOfEmployeesOptionsLabels.value[index]
            : "-";
        },
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: numberOfEmployeesIndices.value,
            options: numberOfEmployeesOptionsLabels.value,
            name: "number_of_employees",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMPLOYEES"),
          onValueChange: (indices: string[]): void => {
            filters.number_of_employees = indices.flatMap((index) =>
              String(numberOfEmployeeIdByIndex(Number(index)))
            );
          },
          numberOfFilters: filters.number_of_employees.length,
        },
      },
      isLendflowUser
        ? {
            label: t("DEALS.TABLE_HEADERS.FUNDING_LENDERS"),
            key: "funded_offer_funders",
            formatModel: true,
            formatter: (model: IApplication) => {
              return model.funded_offer_funder ?? "-";
            },
            filterComponent: {
              component: LfCheckboxDropdown,
              props: {
                modelValue: filters.funded_offer_funders,
                options: fundingLenders.value,
                placeholder: t("DEALS.TABLE_HEADERS.FUNDING_LENDERS"),
                name: "funded_offer_funders",
                enableSearch: true,
                showClearFilter: true,
                apiQueryParam: "search",
                getPaginatedOptions: getFundersDropdownOptions,
                filterIcon: true,
              },
              sortBy: filters.sort_by,
              sortByChange,
              plural: t("DEALS.TABLE_HEADERS.FUNDING_LENDERS"),
              onValueChange: (value: string[]) =>
                (filters.funded_offer_funders = value),
              loadedMore: (value: IFunder[]) =>
                handleFundingLendersOptionsUpdate(value, "loadmore"),
              optionsSearch: (value: IFunder[]) =>
                handleFundingLendersOptionsUpdate(value, "search"),
              numberOfFilters: filters.funded_offer_funders.length,
            },
          }
        : null,
      isLendflowUser
        ? {
            label: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_OFFERS"),
            key: "funders_with_offers",
            component: BadgeList,
            props: {
              showNumOfItems: 3,
              listItemType: "text",
            },
            filterComponent: {
              component: LfCheckboxDropdown,
              props: {
                modelValue:
                  filters[
                    excludableFiltersMap.value.fundersWithOffers.activeMode
                  ],
                options: fundersOptions.value,
                placeholder: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_OFFERS"),
                name: "funders_with_offers",
                enableSearch: true,
                showClearFilter: true,
                apiQueryParam: "search",
                isExcludeModeEnabled:
                  excludableFiltersMap.value.fundersWithOffers.activeMode ===
                  NOT_FUNDERS_WITH_OFFERS,
                getPaginatedOptions: fundersApiService.getFunders,
                filterIcon: true,
              },
              plural: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_OFFERS"),
              onValueChange: (value: string[]) =>
                (filters[
                  excludableFiltersMap.value.fundersWithOffers.activeMode
                ] = value),
              loadedMore: (value: IUser[]) =>
                handleFundersOptionsUpdate(value, "loadmore"),
              optionsSearch: (value: IUser[]) =>
                handleFundersOptionsUpdate(value, "search"),
              excludeModeChanged: (value: boolean) => {
                excludableFiltersMap.value.fundersWithOffers.activeMode = value
                  ? NOT_FUNDERS_WITH_OFFERS
                  : FUNDERS_WITH_OFFERS;

                handleQueryChange("fundersWithOffers");
              },
              numberOfFilters:
                filters[excludableFiltersMap.value.fundersWithOffers.activeMode]
                  .length,
            },
          }
        : null,
      isLendflowUser
        ? {
            label: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_PLACEMENTS"),
            key: "funders_with_placements",
            component: BadgeList,
            props: {
              showNumOfItems: 3,
              listItemType: "text",
            },
            filterComponent: {
              component: LfCheckboxDropdown,
              props: {
                modelValue:
                  filters[
                    excludableFiltersMap.value.fundersWithPlacements.activeMode
                  ],
                options: fundersOptions.value,
                placeholder: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_PLACEMENTS"),
                name: "funders_with_placements",
                enableSearch: true,
                showClearFilter: true,
                apiQueryParam: "search",
                isExcludeModeEnabled:
                  excludableFiltersMap.value.fundersWithPlacements
                    .activeMode === NOT_FUNDERS_WITH_PLACEMENTS,
                getPaginatedOptions: fundersApiService.getFunders,
                filterIcon: true,
              },
              plural: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_PLACEMENTS"),
              onValueChange: (value: string[]) =>
                (filters[
                  excludableFiltersMap.value.fundersWithPlacements.activeMode
                ] = value),
              loadedMore: (value: IUser[]) =>
                handleFundersOptionsUpdate(value, "loadmore"),
              optionsSearch: (value: IUser[]) =>
                handleFundersOptionsUpdate(value, "search"),
              excludeModeChanged: (value: boolean) => {
                excludableFiltersMap.value.fundersWithPlacements.activeMode =
                  value ? NOT_FUNDERS_WITH_PLACEMENTS : FUNDERS_WITH_PLACEMENTS;

                handleQueryChange("fundersWithPlacements");
              },
              numberOfFilters:
                filters[
                  excludableFiltersMap.value.fundersWithPlacements.activeMode
                ].length,
              lazyLoad: true,
            },
          }
        : null,
      {
        label: t("DEALS.TABLE_HEADERS.FIRST_PLACEMENT_TIME"),
        key: "first_placement_datetime",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.first_placement_range = [];
          },
          props: {
            modelValue: filters.first_placement_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.DATE"),
            filterIcon: true,
            name: "first_placement_date",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.FIRST_PLACEMENT_TIME")}: ${
            filters.first_placement_range[0]
          } - ${filters.first_placement_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.first_placement_range = value),
          hasFiltersSet:
            !!filters.first_placement_range[0] &&
            !!filters.first_placement_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.LAST_PLACEMENT_TIME"),
        key: "last_placement_datetime",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.last_placement_range = [];
          },
          props: {
            modelValue: filters.last_placement_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.DATE"),
            filterIcon: true,
            name: "last_placement_date",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.LAST_PLACEMENT_TIME")}: ${
            filters.last_placement_range[0]
          } - ${filters.last_placement_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.last_placement_range = value),
          hasFiltersSet:
            !!filters.last_placement_range[0] &&
            !!filters.last_placement_range[1],
        },
      },

      {
        label: t("DEALS.TABLE_HEADERS.HIGHEST_OFFER_AMOUNT"),
        key: "highest_offer_amount",
        formatModel: true,
        formatter: (val: IApplication) =>
          moneyToTwoDecimal(val.highest_offer_amount ?? 0),
        columnClass: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.highest_offer_amount_range,
            name: "highest_offer_amount",
            onChange: (value: number[]) => {
              filters.highest_offer_amount_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.HIGHEST_OFFER_AMOUNT"),
            unit: Unit.Dollar,
            mask: "#########",
          },
          plural: t("DEALS.TABLE_HEADERS.HIGHEST_OFFER_AMOUNT"),
          sortBy: filters.sort_by,
          sortByChange: () => sortByChange("highest_offer_amount"),
          numberOfFilters: filters.highest_offer_amount_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TIME_IN_BUSINESS"),
        key: "time_in_business",
        formatter: formatMonth,
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.time_in_business_range,
            name: "time_in_business",
            onChange: (value: number[]) => {
              filters.time_in_business_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.TIME_IN_BUSINESS"),
            unit: Unit.Months,
          },
          plural: t("DEALS.TABLE_HEADERS.TIME_IN_BUSINESS"),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.time_in_business_range?.length,
        },
      },
      {
        label: t("DEALS.LEXIS_NEXIS.UCC.TIME_ZONE"),
        key: "business.address.time_zone",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.business_time_zones,
            options: timeZones.value,
            name: "business_time_zones",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: t("DEALS.LEXIS_NEXIS.UCC.TIME_ZONE"),
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]): void => {
            filters.business_time_zones = value;
          },
          numberOfFilters: filters.business_time_zones.length,
        },
      },
      {
        label: t("CREDIT.STATED_SCORE"),
        key: "personal_information.credit_score",
        columnClass: "text-right",
        formatter: (creditScoreKey: string) =>
          creditScores.value[creditScoreKey],
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.primary_owner_stated_credit_scores,
            options: creditScores.value,
            name: "primary_owner_stated_credit_scores",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: t("CREDIT.STATED_SCORE"),
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]): void => {
            filters.primary_owner_stated_credit_scores = value;
          },
          numberOfFilters: filters.primary_owner_stated_credit_scores.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.SCORECARD_RESULT_STATUS"),
        key: "score_card_result.status",
        component: ScoreCardResultStatusPill,
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.latest_score_card_statuses,
            options: latestScoreCardStatuses.value,
            name: "latest_score_card_statuses",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: t("DEALS.TABLE_HEADERS.SCORECARD_RESULT_STATUS"),
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: number[]): void => {
            filters.latest_score_card_statuses = value;
          },
          numberOfFilters: filters.latest_score_card_statuses.length,
        },
      },
      {
        label: t("CREDIT.ACTUAL_SCORE"),
        key: "personal_information.actual_credit_score",
        columnClass: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.primary_owner_actual_credit_score_range,
            name: "primary_owner_actual_credit_score",
            onChange: (value: number[]) => {
              filters.primary_owner_actual_credit_score_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("CREDIT.ACTUAL_SCORE"),
            unit: Unit.Points,
            mask: "###",
          },
          plural: t("CREDIT.ACTUAL_SCORE"),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters:
            filters.primary_owner_actual_credit_score_range?.length,
        },
      },
      {
        label: `${t("ACTIVITY.FILTERS.PRIORITY")} ${t("DEALS.TRACKING.OFFER_STATUS")}`,
        key: "priority_offer_status",
        component: "offer-status-pill",
        props: {
          class: "h-6",
        },
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.priority_offer_statuses,
            options: offerStatuses.value,
            name: "priority_offer_statuses",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("ACTIVITY.FILTERS.PRIORITY")} ${t("DEALS.TRACKING.OFFER_STATUS")}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: number[]): void => {
            filters.priority_offer_statuses = value;
          },
          numberOfFilters: filters.priority_offer_statuses.length,
        },
      },
      {
        label: `${t("LOGS.LAST_UPDATED")} ${t("DEALS.TRACKING.OFFER_STATUS")}`,
        key: "last_updated_offer_status",
        component: "offer-status-pill",
        props: {
          class: "h-6",
        },
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.last_updated_offer_statuses,
            options: offerStatuses.value,
            name: "last_updated_offer_statuses",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("LOGS.LAST_UPDATED")} ${t("DEALS.TRACKING.OFFER_STATUS")}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: number[]): void => {
            filters.last_updated_offer_statuses = value;
          },
          numberOfFilters: filters.last_updated_offer_statuses.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.AUTOMATION"),
        key: "latest_workflow_orchestration_log_status",
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.latest_workflow_orchestration_log_statuses,
            options: automationOutcomes.value,
            placeholder: t("DEALS.TABLE_HEADERS.AUTOMATION"),
            name: "latest_workflow_orchestration_log_statuses",
            showClearFilter: true,
            filterIcon: true,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: t("DEALS.TABLE_HEADERS.AUTOMATION"),
          onValueChange: (value: string[]) =>
            (filters.latest_workflow_orchestration_log_statuses = value),
          numberOfFilters:
            filters.latest_workflow_orchestration_log_statuses.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.EMAIL_VALIDATION_MESSAGE"),
        component: EmailValidationMessageCell,
        key: "latest_email_validation_message",
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.has_latest_email_validation_error,
            options: YES_NO_OPTIONS,
            name: "has_latest_email_validation_error",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: t("DEALS.TABLE_HEADERS.EMAIL_VALIDATION_MESSAGE"),
          onChange: (value: number) =>
            (filters.has_latest_email_validation_error = value),
          hasFiltersSet: filterHasValue(
            filters.has_latest_email_validation_error
          ),
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_SENT"),
        key: "number_of_emails_sent",
        columnClass: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.number_of_emails_sent_range,
            name: "number_of_emails_sent",
            onChange: (value: number[]) => {
              filters.number_of_emails_sent_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_SENT"),
            allowMaxZero: true,
          },
          plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_SENT", 2),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.number_of_emails_sent_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_RECEIVED"),
        key: "number_of_emails_received",
        columnClass: "text-right",
        filterComponent: {
          initialRender: false,
          component: MinAndMaxFilter,
          props: {
            modelValue: filters.number_of_emails_received_range,
            name: "number_of_emails_received",
            onChange: (value: number[]) => {
              filters.number_of_emails_received_range = value;
            },
            filterIcon: true,
            showClearFilter: true,
            label: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_RECEIVED"),
            allowMaxZero: true,
          },
          plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_RECEIVED", 2),
          sortBy: filters.sort_by,
          sortByChange,
          numberOfFilters: filters.number_of_emails_received_range?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.LAST_EMAIL_SENT"),
        key: "latest_email_sent.timestamp",
        component: LastEmailSent,

        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.last_email_sent_date_range = [];
          },
          props: {
            modelValue: filters.last_email_sent_date_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.DATE"),
            filterIcon: true,
            name: "last_email_sent_date",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.LAST_EMAIL_SENT")}: ${
            filters.last_email_sent_date_range[0]
          } - ${filters.last_email_sent_date_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.last_email_sent_date_range = value),
          hasFiltersSet:
            !!filters.last_email_sent_date_range[0] &&
            !!filters.last_email_sent_date_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.LAST_EMAIL_SENT_NAME"),
        key: "latest_email_sent.template_name",
        component: TruncatedTooltipableText,
        filterComponent: {
          initialRender: true,
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.last_email_sent_name,
            options: lastEmailSentNames.value,
            placeholder: t("DEALS.TABLE_HEADERS.LAST_EMAIL_SENT_NAME"),
            name: "last_email_sent_name",
            enableSearch: true,
            showClearFilter: true,
            getPaginatedOptions: customizationApiService.getEmailTemplates,
            apiQueryParam: "search",
            filterIcon: true,
            lazyLoad: true,
          },
          sortBy: filters.sort_by,
          sortByChange,
          plural: t("DEALS.TABLE_HEADERS.LAST_EMAIL_SENT_NAME", 2),
          onValueChange: (value: string[]) =>
            (filters.last_email_sent_name = value),
          loadedMore: (value: IClient[]) =>
            handleLastEmailSentNameOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IClient[]) =>
            handleLastEmailSentNameOptionsUpdate(value, "search"),
          numberOfFilters: filters.last_email_sent_name.length,
        },
      },
      {
        label: t("COMMON.NOTE"),
        key: "latest_note.comment",
        component: RecentNote,
        columnClass: "min-w-63 max-w-100",
      },
      {
        label: `${t("COMMON.APPLICATION")} ${t("COMMON.NAME")}`,
        key: "application_name",
        component: DealNameField,
        props: {
          includeBackTo: true,
          showCopyButtons: false,
        },
        columnClass: "w-full min-w-64",
      },
      {
        label: `${t("COMMON.APPLICANT")} ${t("COMMON.NAME")}`,
        key: "applicant_name",
      },
      {
        label: t("SMART_FILTERS.ASSIGNED_ONBOARDING_CALLS"),
        key: "assigned_onboarding_call_sessions",
        component: CallCountText,
        filterComponent: {
          initialRender: false,
          component: CallsFilter,
          props: {
            name: "assigned_onboarding_call_sessions",
            numberOfCallsRange: filters.assigned_onboarding_calls,
            dateRange: filters.created_at_assigned_onboarding_call,
            showClearFilter: true,
            filterIcon: true,
            alwaysShow: true,
          },
          onChange: (value: CallsFilterPayload) => {
            filters.assigned_onboarding_calls = value.number_of_calls;
            filters.created_at_assigned_onboarding_call =
              value.created_at_dates;
          },
          hasFiltersSet:
            !!filters.assigned_onboarding_calls.length ||
            !!filters.created_at_assigned_onboarding_call.length,
          plural: t("SMART_FILTERS.ASSIGNED_ONBOARDING_CALLS"),
          sortByChange,
          sortBy: filters.sort_by,
        },
      },
      {
        label: t("SMART_FILTERS.TOTAL_ONBOARDING_CALLS"),
        key: "total_onboarding_call_sessions",
        component: CallCountText,
        filterComponent: {
          initialRender: false,
          component: CallsFilter,
          props: {
            name: "total_onboarding_call_sessions",
            numberOfCallsRange: filters.total_onboarding_calls,
            dateRange: filters.created_at_onboarding_call,
            showClearFilter: true,
            filterIcon: true,
            alwaysShow: true,
          },
          onChange: (value: CallsFilterPayload) => {
            filters.total_onboarding_calls = value.number_of_calls;
            filters.created_at_onboarding_call = value.created_at_dates;
          },
          hasFiltersSet:
            !!filters.total_onboarding_calls.length ||
            !!filters.created_at_onboarding_call.length,
          plural: t("SMART_FILTERS.TOTAL_ONBOARDING_CALLS"),
          sortByChange,
          sortBy: filters.sort_by,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TASK_CREATED"),
        key: "closest_due_task",
        component: HasDataDisplay,
        props: {
          type: EnrichmentData.task,
          onEnrichmentDataLoaded: saveTaskData,
        },
        filterComponent: {
          initialRender: false,
          component: "lf-dropdown",
          props: {
            modelValue: filters.task_is_created,
            options: YES_NO_OPTIONS,
            name: "task_created",
            filterIcon: true,
            showClearFilter: true,
          },
          plural: `${t("DEALS.TABLE_HEADERS.TASK_CREATED")}-${
            filterHasValue(filters.task_is_created)
              ? YES_NO_OPTIONS[
                  filters.task_is_created as keyof typeof YES_NO_OPTIONS
                ]
              : ""
          }`,
          onChange: (value: number) => (filters.task_is_created = value),
          hasFiltersSet: filterHasValue(filters.task_is_created),
        },
        isSubColumnLoading: subColumnLoading.value.closest_due_task_loading,
        subColumns: subColumnsVisibility.value.closest_due_task
          ? [
              {
                label: t("DEALS.SUBCOLUMNS.ASSIGNEE"),
                key: "tasksData.assignee",
                component: NameList,
                props: {
                  textClass: "truncate",
                },
              },
              {
                label: t("DEALS.SUBCOLUMNS.CREATED_BY"),
                key: "tasksData.created_by",
                component: NameList,
                props: {
                  textClass: "truncate",
                },
              },
              {
                label: t("DEALS.SUBCOLUMNS.DESCRIPTION"),
                key: "tasksData.body",
                component: DescriptionsList,
                columnClass: "min-w-46",
              },
              {
                label: t("DEALS.SUBCOLUMNS.PRIORITY_LEVEL"),
                key: "tasksData.priority_level",
                component: PriorityLevelList,
              },
              {
                label: t("DEALS.SUBCOLUMNS.STATUS"),
                key: "tasksData.status",
                columnClass: "min-w-46",
                component: TaskStatus,
              },
              {
                label: t("DEALS.SUBCOLUMNS.DUE_AT"),
                key: "tasksData.due_at",
                component: DatesList,
                props: {
                  dateClass: "my-1",
                },
              },
            ]
          : [],
      },
      {
        label: t("DEALS.TABLE_HEADERS.TASK_DESCRIPTION"),
        key: "closest_due_task.body",
      },
      {
        label: t("DEALS.TABLE_HEADERS.REPAYMENT_PERCENTAGE_RANGE"),
        key: "repayment_percentage",
        props: {
          class: "ml-auto",
        },
        component: LfTag,
        filterComponent: {
          initialRender: false,
          component: RangeFilter,
          onClear: () => {
            filters.repayment_percentage_range = [];
          },
          props: {
            modelValue: filters.repayment_percentage_range,
            filterIcon: true,
            placeholder: t("COMMON.PERCENTAGE"),
            name: "repayment_percentage_range",
            class: "bg-white shadow-lg rounded-md p-2 min-w-64",
            isSmartView: false,
            minAllowed: 0,
            maxAllowed: 100,
          },
          plural: `${t("DEALS.TABLE_HEADERS.REPAYMENT_PERCENTAGE_RANGE")}: ${
            filters.repayment_percentage_range[0]
          } - ${filters.repayment_percentage_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: number[]) =>
            (filters.repayment_percentage_range = value),
          hasFiltersSet:
            !!filters.repayment_percentage_range[0] &&
            !!filters.repayment_percentage_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TASK_ASSIGNEE", 1),
        key: "closest_due_task.assignee",
        component: TaskAssignee,
        filterComponent: {
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[excludableFiltersMap.value.taskAssignees.activeMode],
            options: assigneeOptions.value,
            placeholder: t("DEALS.TABLE_HEADERS.TASK_ASSIGNEE", 2),
            name: "task_assignees",
            enableSearch: true,
            showClearFilter: true,
            apiQueryParam: "search",
            isExcludeModeEnabled:
              excludableFiltersMap.value.taskAssignees.activeMode ===
              NOT_TASK_ASSIGNEES,
            getPaginatedOptions: (params: WorkflowsViewFilters) => {
              if (isClient) {
                if (!client.value?.id) {
                  return;
                }
                return usersApiService.getClientUsers(client.value.id, {
                  ...params,
                  with_preset: 0,
                });
              }
              return usersApiService.getEmployees({
                ...params,
                with_preset: 0,
              });
            },
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.TASK_ASSIGNEE", 2),
          onValueChange: (value: string[]) =>
            (filters[excludableFiltersMap.value.taskAssignees.activeMode] =
              value),
          loadedMore: (value: IUser[]) =>
            handleAssigneesOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IUser[]) =>
            handleAssigneesOptionsUpdate(value, "search"),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.taskAssignees.activeMode = value
              ? NOT_TASK_ASSIGNEES
              : TASK_ASSIGNEES;

            handleQueryChange("taskAssignees");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.taskAssignees.activeMode].length,
          lazyLoad: true,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.LAST_CALL_BY"),
        key: "last_call_session.user",
        component: TaskAssignee,
        filterComponent: {
          component: "lf-checkbox-dropdown",
          props: {
            modelValue: filters.last_call_by,
            options: assigneeOptions.value,
            placeholder: t("DEALS.TABLE_HEADERS.LAST_CALL_BY"),
            name: "last_call_by",
            enableSearch: true,
            sort: true,
            showClearFilter: true,
            apiQueryParam: "search",
            getPaginatedOptions: (params: WorkflowsViewFilters) => {
              if (isClient) {
                if (!client.value?.id) {
                  return;
                }
                return usersApiService.getClientUsers(client.value.id, {
                  ...params,
                  with_preset: 0,
                });
              }
              return usersApiService.getEmployees({
                ...params,
                with_preset: 0,
              });
            },
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.LAST_CALL_BY"),
          onValueChange: (value: string[]) => (filters.last_call_by = value),
          loadedMore: (value: IUser[]) =>
            handleAssigneesOptionsUpdate(value, "loadmore"),
          optionsSearch: (value: IUser[]) =>
            handleAssigneesOptionsUpdate(value, "search"),
          numberOfFilters: filters.last_call_by.length,
          lazyLoad: true,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TASK_DUE_DATE"),
        key: "closest_due_task.due_at",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.task_due_date_range = [];
          },
          props: {
            modelValue: filters.task_due_date_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "task_due_date_range",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.TASK_DUE_DATE")}: ${
            filters.task_due_date_range[0]
          } - ${filters.task_due_date_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.task_due_date_range = value),
          hasFiltersSet:
            !!filters.task_due_date_range[0] &&
            !!filters.task_due_date_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TASK_CREATED_DATE"),
        key: "closest_due_task.created_at",
        component: DateAndTimeDisplay,
        filterComponent: {
          initialRender: false,
          component: "lf-date-picker",
          onClear: () => {
            filters.task_created_date_range = [];
          },
          props: {
            modelValue: filters.task_created_date_range,
            formatter: formatter.value,
            datePlaceholder: t("COMMON.THIS_YEAR"),
            filterIcon: true,
            name: "task_created_date_range",
            showOnMount: true,
            disabled: dealsLoading.value, // workaround for problems with programmatically setting litepie-datepicker
            showTriggerButton: false,
          },
          plural: `${t("DEALS.TABLE_HEADERS.TASK_CREATED_DATE")}: ${
            filters.task_created_date_range[0]
          } - ${filters.task_created_date_range[1]}`,
          sortBy: filters.sort_by,
          sortByChange,
          onValueChange: (value: string[]) =>
            (filters.task_created_date_range = value),
          hasFiltersSet:
            !!filters.task_created_date_range[0] &&
            !!filters.task_created_date_range[1],
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TASK_PRIORITY"),
        key: "closest_due_task.priority_level",
        component: TaskPriority,
        filterComponent: {
          component: LfCheckboxDropdown,
          props: {
            renderComponent: PriorityOption,
            modelValue:
              filters[excludableFiltersMap.value.taskPriorityLevels.activeMode],
            options: dropDownTaskPriorityLevelsOptions,
            placeholder: t("DEALS.TABLE_HEADERS.TASK_PRIORITY"),
            name: "task_priority_levels",
            enableSearch: false,
            showClearFilter: true,
            isExcludeModeEnabled:
              excludableFiltersMap.value.taskPriorityLevels.activeMode ===
              NOT_TASK_PRIORITY_LEVELS,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.TASK_PRIORITY", 2),
          onValueChange: (value: string[]) =>
            (filters[excludableFiltersMap.value.taskPriorityLevels.activeMode] =
              value),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.taskPriorityLevels.activeMode = value
              ? NOT_TASK_PRIORITY_LEVELS
              : TASK_PRIORITY_LEVELS;

            handleQueryChange("taskPriorityLevels");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.taskPriorityLevels.activeMode]
              .length,
          lazyLoad: true,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.TASK_STATUS"),
        key: "closest_due_task",
        columnClass: "min-w-46",
        component: TaskStatus,
        placeholder: [],
        formatter: (value: { status: TaskStatuses; id: number | null }) =>
          convertToArray(value),
        filterComponent: {
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[excludableFiltersMap.value.taskStatuses.activeMode],
            options: dropDownTaskStatusesOptions,
            placeholder: t("DEALS.TABLE_HEADERS.TASK_STATUS"),
            name: "task_statuses",
            enableSearch: false,
            showClearFilter: true,
            isExcludeModeEnabled:
              excludableFiltersMap.value.taskStatuses.activeMode ===
              NOT_TASK_STATUSES,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.TASK_STATUS", 2),
          onValueChange: (value: string[]) =>
            (filters[excludableFiltersMap.value.taskStatuses.activeMode] =
              value),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.taskStatuses.activeMode = value
              ? NOT_TASK_STATUSES
              : TASK_STATUSES;

            handleQueryChange("taskStatuses");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.taskStatuses.activeMode].length,
          lazyLoad: true,
        },
      },
      {
        label: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.PRODUCTS_REQUESTED"),
        key: "products_requested",
        component: BadgeList,
        props: {
          showNumOfItems: 3,
          listItemType: "text",
        },
        filterComponent: {
          component: LfCheckboxDropdown,
          props: {
            modelValue: filters.products_requested,
            options: productsRequestedOptions.value,
            placeholder: t(
              "DEALS.DEAL_PROGRESS.BUSINESS_INFO.PRODUCTS_REQUESTED"
            ),
            name: "products_requested",
            enableSearch: true,
            showClearFilter: true,
            apiQueryParam: "search",
            getPaginatedOptions: (params: Record<string, string>) => {
              return offerTemplateService.getTemplates({
                ...params,
                owned: 1,
                has_applications: 1,
                distinct_name: 1,
              });
            },
            filterIcon: true,
          },
          plural: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.PRODUCTS_REQUESTED"),
          onValueChange: (value: string[]) =>
            (filters.products_requested = value),
          loadedMore: (value: OfferTemplate[]) =>
            handleProductsRequested(value, "loadmore"),
          optionsSearch: (value: OfferTemplate[]) =>
            handleProductsRequested(value, "search"),
          numberOfFilters: filters.products_requested?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.NAICS_CODES"),
        class: "w-46 break-words",
        key: "business.naics_code",
        filterComponent: {
          initialRender: false,
          component: NaicsCodesDropdown,
          props: {
            modelValue: filters.naics_codes,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.NAICS_CODES"),
          onValueChange: (value: string[]) => (filters.naics_codes = value),
          numberOfFilters: filters.naics_codes.length,
          lazyLoad: true,
          lazyAction: {
            action: "options/getNaicsCodes",
          },
        },
      },
      {
        label: documentsFilterLabel.value,
        key: "required_documents",
        component: BadgeList,
        columnClass: "min-w-max line-clamp-2",
        props: {
          showNumOfItems: 2,
          badgeClass: "h-5 leading-4 uppercase text-xxs font-semibold",
          pillClass: "h-5",
        },
        formatter: (docs: RequiredDocument[]) =>
          docs.map(
            (doc) => `${doc.name} ${doc.uploaded_count}/${doc.required_count}`
          ),
        filterComponent: {
          component: DocumentsFilter,
          props: {
            class: "h-10 min-w-60",
            modelValue: filters.required_documents,
            documentsCompleted: filters.completed_documents,
            filterIcon: true,
            onDocumentsChanged: (value: string) => {
              filters.completed_documents = value;
            },
          },
          onValueChange: (value: number[]) => {
            filters.required_documents = value;
            if (!value.length) {
              filters.completed_documents = "";
            }
          },
          plural: documentsFilterLabel.value,
          hasFiltersSet:
            !!filters.required_documents?.length ||
            filters.completed_documents.length,
          numberOfFilters: filters.required_documents?.length,
        },
      },
      {
        label: t("DEALS.TABLE_HEADERS.ALL_ACTIVITY"),
        key: "latest_communication_log",
        component: DealCommunicationLog,
      },
      {
        label: t("DEALS.TABLE_HEADERS.PRODUCT_TYPE_FUNDED"),
        key: "product_type_funded",
        filterComponent: {
          initialRender: false,
          component: LfCheckboxDropdown,
          props: {
            modelValue:
              filters[excludableFiltersMap.value.productTypeFunded.activeMode],
            options: productTypes.value,
            placeholder: t("DEALS.TABLE_HEADERS.PRODUCT_TYPE_FUNDED"),
            name: "product_type_funded",
            enableSearch: false,
            showClearFilter: true,
            isExcludeModeEnabled:
              excludableFiltersMap.value.productTypeFunded.activeMode ===
              NOT_PRODUCT_TYPE_FUNDED,
            filterIcon: true,
          },
          plural: t("DEALS.TABLE_HEADERS.PRODUCT_TYPE_FUNDED"),
          onValueChange: (value: string[]) =>
            (filters[excludableFiltersMap.value.productTypeFunded.activeMode] =
              value),
          excludeModeChanged: (value: boolean) => {
            excludableFiltersMap.value.productTypeFunded.activeMode = value
              ? NOT_PRODUCT_TYPE_FUNDED
              : PRODUCT_TYPE_FUNDED;

            handleQueryChange("productTypeFunded");
          },
          numberOfFilters:
            filters[excludableFiltersMap.value.productTypeFunded.activeMode]
              ?.length ?? 0,
          lazyLoad: true,
        },
      },
    ].filter(Boolean) as IDealsColumn[];

    return filteredColumns;
  });

  const handleQueryChange = (key: keyof ActiveFilterWithExceptionMode) => {
    const { activeMode, negative, positive } = excludableFiltersMap.value[key];

    const query = JSON.parse(JSON.stringify(route.query));

    // get query value for previous mode
    const queryValue =
      activeMode === negative ? query[`${positive}[]`] : query[`${negative}[]`];

    if (queryValue === undefined) {
      return;
    }

    const oldMode = activeMode === negative ? positive : negative;
    delete query[`${oldMode}[]`];

    const queryArray = convertToArray(queryValue) as string[] & number[];
    query[activeMode] = queryArray;

    filters[activeMode] = queryArray;
    filters[oldMode] = [];

    router.push({ query });
  };

  watch(
    () => route.query,
    (updatedQuery) => {
      getObjectKeys(excludableFiltersMap.value).forEach((key) => {
        const filter = excludableFiltersMap.value[key];
        filter.activeMode = updatedQuery[`${filter.negative}[]`]
          ? filter.negative
          : filter.positive;
      });
    }
  );

  return {
    allColumns,
    appliedWorkflows,
    subColumnLoading,
    subColumnsVisibility,
    enrichedDealsData,
  };
};
